/**
 * TO Appointments — the day, as one screen (module 01, `03-appointments.md` Part A).
 *
 * This component owns the day's appointment data and the five writes against it. Everything
 * below it draws: the Fixture List, and the three tables derived from it. That division is
 * the design, not a tidy-up — §A5's matrices are *"derived live from the fixture list on
 * every edit, never stored"*, so there is exactly one place the appointments live and three
 * places they are counted.
 *
 * Six rules this component exists to keep:
 *
 *  1. **The day is the unit of work (§A1).** One day, both competitions, every fixture with
 *     its three technical slots. U1, U2, RU and VU are not here and are not an oversight:
 *     they belong to the Umpires Managers (§B1) and the API does not serve them on this
 *     route, so the screen cannot draw them.
 *
 *  2. **The server's reply is authoritative.** Draft, fill, edit, withdraw, confirm, lock and
 *     unlock all answer with the whole day recomputed (Part C invariant 1). An edit is
 *     optimistic only so the cell moves at once, and is replaced the moment the server
 *     answers. A refusal puts the cell back exactly as it was — and because the three tables
 *     below are derived from that same state, they go back with it.
 *
 *  2a. **Filling an empty slot and changing a filled one are the same act.** §A6 puts a
 *     picker on every slot; that an empty slot is created (`POST /appointments`) and a filled
 *     one is amended (`PATCH /appointments/:id`) is the API's business, not the TD's, and the
 *     §A7 two-step below is identical on both.
 *
 *  3. **The country conflict takes two deliberate steps (§A7).** The first write is sent
 *     without `acceptCountryConflict` and is *expected* to be refused. That refusal is not an
 *     error state — it is step one. The screen then names the conflict and asks, and only a
 *     second, explicit confirmation re-sends with the flag. The slot then carries an
 *     `Overridden` marker and stays flagged, because §A7 records an override rather than
 *     hiding it.
 *
 *  4. **A confirmed slot is immutable, and a frozen fixture more so.** CC-Q25 signs a slot
 *     off; §A4 rule 6 freezes a fixture TMS has marked Official. Neither renders a control
 *     the API would refuse — each says instead why it cannot be changed.
 *
 *  4a. **A locked match is the record, and reopening it is a deliberate act (§A11, RUN 82).**
 *     The Technical Delegate's rulings of 16 September 2026: `Mark Day Locked` declares a day's
 *     TO, SJ and TJ to be what TMS holds — keyed in by hand while there is no push — and a
 *     locked appointment is read-only and counts as who officiated. `Unlock` reopens ONE match,
 *     clears its sign-off and marks it `Needs Re-Push` until the day is locked again. Both ask
 *     before they write, in the page's one dialogue idiom, because neither is a draft.
 *
 *     RUN 82A (QA pass 1) — and each question is asked on its own. While any question is open
 *     the two presses that open these do nothing, and neither answer can be given while another
 *     write is out, so an open `Unlock M01` cannot become `Unlock M02` under the TD's hand.
 *
 *     RUN 82D (QA pass 2) — and the rule is now the whole page's, not the lock layer's. An open
 *     question makes the workbench behind it inert, closes the officials popover, and no press on
 *     the page opens a second question; no question's answer can be given while a write is out,
 *     and no write starts while another is out. The lock layer's two questions are modal to
 *     assistive technology as well, with Tab kept on their two buttons. The probe that found it:
 *     `Clear Drafts` opened over an open lock question, was answered while the lock was out, and
 *     its reply re-enabled `Mark Day Locked` in time for a second lock.
 *
 *  5. **The §F anomaly is not an error.** `fixture_already_official` is an amber advisory
 *     note: TMS is the record, nothing is blocked, and the screen must not dress it as a
 *     breach.
 *
 *  6. **Every panel filters itself** (Part C invariant 2). The four competition tab bars are
 *     four independent pieces of state on purpose: a tab that silently moved three other
 *     panels would show each of them in a state its own tab bar denies.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import type {
  AppointmentWarning,
  TechnicalSlot,
  TmsCompetitionId,
  TmsMatchDbId,
} from "@hockeytd/shared";

import {
  ApiError,
  deleteAppointmentSlot,
  fetchAppointments,
  patchAppointmentSlot,
  postAppointmentSlot,
  postAppointmentsConfirm,
  postAppointmentsDraft,
  postAppointmentsClearDrafts,
  postAppointmentsDraftStage,
  postAppointmentsLock,
  postAppointmentsUnlock,
  fetchAppointmentsSchedule,
  fetchAppointmentPushReceipts,
  newPressId,
  prepareExtensionDayPush,
  prepareExtensionPush,
  releaseExtensionPush,
  reportExtensionPush,
  type AppointmentScope,
} from "../../api/client.js";
import type {
  AppointmentFixtureBody,
  AppointmentSlotBody,
  AppointmentsViewBody,
  ClearDraftsDayBody,
  ClearDraftsResponseBody,
  ConfirmResponseBody,
  DayPushReceiptsBody,
  DraftResponseBody,
  ExtensionPushDayPlanBody,
  ExtensionPushPlanBody,
  ExtensionPushReleaseBody,
  ExtensionPushReportBody,
  ExtensionPushResultMessage,
  LockResponseBody,
  OfficialView,
  SkippedOfficialMatchBody,
  StageDraftDayBody,
  StageDraftResponseBody,
  UnlockResponseBody,
} from "../../api/types.js";
import { useTournament } from "../../app/session.js";
import { anchorDay, dayLabel, tournamentToday } from "../schedule/matchDays.js";
import { exportWorkbook, type WorkbookExporter } from "../export/render.js";
import { technicalFixtureRows, technicalMatrixRows, scopeLine } from "../export/rows.js";
import { buildTechnicalWorkbook, exportFilename, exportNotice } from "../export/workbook.js";
import {
  CLEAR_DRAFTS_ID,
  DRAFT_STAGE_ID,
  FixtureListPanel,
  LOCK_DAY_ID,
  REDRAFT_ID,
  rowKey,
  slotErrorId,
  slotNameId,
  unlockButtonId,
  withdrawButtonId,
} from "./FixtureListPanel.js";
import { WarningLine } from "./FixtureWarnings.js";
import { MatrixPanel } from "./MatrixPanel.js";
import { OfficialPicker } from "./OfficialPicker.js";
import { TeamsCoveragePanel } from "./TeamsCoveragePanel.js";
import { TechTablePanel } from "./TechTablePanel.js";
import {
  buildMatrix,
  buildTeams,
  buildTech,
  competitionLabel,
  derivedCellValues,
  filterFixtures,
  matrixOfficials,
  type CompetitionFilter,
  type RoleFilter,
  type StageFilter,
} from "./derive.js";
import { useCellFlash } from "./useCellFlash.js";
import {
  countOf,
  daySummaryCounts,
  hasRuleBreach,
  isAnomaly,
  type PickerOption,
} from "./vocabulary.js";
import {
  PUSH_DAY_BUTTON_ID,
  PUSH_DAY_OUTCOME_ID,
  PUSH_OUTCOME_ID,
  TmsDayPushQuestion,
  TmsPushActions,
  TmsPushQuestion,
  TmsPushResultStrip,
  pushButtonId,
  type TmsDayPushFlowState,
  type TmsPushAnsweredEnding,
  type TmsPushFlowState,
  type TmsPushStripState,
} from "./TmsPushDialog.js";
import {
  ABSENT_AT_THE_PRESS,
  answerHeadline,
  competitionsOnDay,
  dayAnswerIsFailure,
  dayAnswerNotice,
  dayPrepareRefusal,
  isMovedOnPushRefusal,
  noResultText,
  prepareRefusal,
  pushActionViews,
  pushBatchPreview,
  pushDayActionView,
  pushDayPreview,
  releaseRefusal,
  releasedText,
  reportRefusal,
  type PushPresenceState,
} from "./tmsPushCopy.js";
import { LIVE_TMS_PUSH_BRIDGE, type TmsPushBridge } from "./tmsPushExtension.js";
import {
  DEFAULT_TMS_PUSH_TIMING,
  SESSION_TMS_DAY_PUSH_MEMORY,
  SESSION_TMS_PUSH_MEMORY,
  holdLapsed,
  recoverTmsDayPush,
  recoverTmsPush,
  reportTmsDayPushAgain,
  reportTmsPush,
  runTmsDayPush,
  runTmsPush,
  steadyClock,
  type RememberedPress,
  type TmsDayPushEnding,
  type TmsDayPushFlowDeps,
  type TmsDayPushMemory,
  type TmsPushEnding,
  type TmsPushFlowDeps,
  type TmsPushMemory,
  type TmsPushTiming,
} from "./tmsPushFlow.js";

/**
 * The five writes, behind one seam. The real implementations are the API client's; the
 * indirection exists so a component test can drive the panel without a network, not so the
 * transport can be swapped at runtime.
 */
export interface AppointmentWriter {
  draft: (scope: AppointmentScope) => Promise<DraftResponseBody>;
  /**
   * §A8's *"all pool matches up front"* — the whole stage, day by day, in one press.
   *
   * `rotationOffset` is RUN 38's `Re-Draft`: the engine's tie-break rotation, which produces a
   * different but equally rule-abiding arrangement. Omitted by the plain stage draft, which is
   * variant 0.
   */
  draftStage: (
    scope: AppointmentScope,
    rotationOffset?: number,
  ) => Promise<StageDraftResponseBody>;
  /** The rehearsal control — a day, or the stage, back to blank. */
  clearDrafts: (scope: AppointmentScope, kind: "day" | "stage") => Promise<ClearDraftsResponseBody>;
  create: (body: {
    tmsMatchDbId: TmsMatchDbId;
    slot: TechnicalSlot;
    tmsPersonId: string;
    acceptCountryConflict?: boolean;
  }) => Promise<AppointmentsViewBody>;
  patch: (
    appointmentId: string,
    body: { tmsPersonId: string; acceptCountryConflict?: boolean },
  ) => Promise<AppointmentsViewBody>;
  withdraw: (appointmentId: string) => Promise<AppointmentsViewBody>;
  confirm: (scope: AppointmentScope) => Promise<ConfirmResponseBody>;
  /**
   * RUN 82 — `Mark Day Locked` (§A11): the day's TO, SJ and TJ, in both competitions, marked as
   * the record TMS holds. A DAY rather than a scope, because the route takes nothing narrower.
   */
  lock: (day: string) => Promise<LockResponseBody>;
  /** RUN 82 — reopen one locked match for a correction (§A11). Answers with that match's day. */
  unlock: (tmsMatchDbId: TmsMatchDbId) => Promise<UnlockResponseBody>;
  /**
   * RUN 37 — re-read the whole schedule, after a press that wrote across days.
   *
   * Optional, and optional on purpose: it is a READ, so a component test that drives the five
   * writes has nothing to prove by stubbing it, and a writer without it simply leaves the
   * backdrop as it was. The live writer has it, and it is what makes ten drafted days appear
   * as ten drafted days rather than as one.
   */
  readSchedule?: () => Promise<AppointmentsViewBody>;
  /**
   * RUN 82A — re-read ONE day, after an `Unlock` the server refused because the match had moved
   * on under the screen (§A11): another session locked, unlocked or TMS froze it first.
   *
   * Optional for exactly the reason `readSchedule` is: it is a READ, and a writer without it
   * leaves the row as it was drawn, beside the server's own words for why the press failed.
   */
  readDay?: (scope: AppointmentScope) => Promise<AppointmentsViewBody>;
  /**
   * RUN 84 - the extension press's requests (`12-tms-push-extension.md` sections 5 and 6b): the
   * prepare, which answers the plan handed to the TMS Push extension; the report, which carries the
   * extension's result back exactly as it was sent; and the release, asked only when the plan
   * certainly never reached the extension.
   */
  preparePush: (body: {
    day: string;
    competitionId: TmsCompetitionId;
    pressId: string;
  }) => Promise<ExtensionPushPlanBody>;
  reportPush: (body: {
    pressId: string;
    result: ExtensionPushResultMessage;
  }) => Promise<ExtensionPushReportBody>;
  releasePush: (body: { pressId: string }) => Promise<ExtensionPushReleaseBody>;
  /**
   * The day push (`12-tms-push-extension.md` section 6c; Decision #5 = B, 19 Sep 2026) - every
   * competition of one day prepared in one request, one ordinary extension press each; reported,
   * released and acknowledged one press at a time through `reportPush` and `releasePush` above.
   */
  preparePushDay: (body: {
    day: string;
    presses: readonly { competitionId: TmsCompetitionId; pressId: string }[];
  }) => Promise<ExtensionPushDayPlanBody>;
  /**
   * RUN 84 (frontend review L9) - a day's push receipts, read before the page says a remembered press
   * left no result: it may have been reported by another tab, or before a reload. A read; optional,
   * like the other reads - without it the page says only what the extension said.
   */
  readPushReceipts?: (day: string) => Promise<DayPushReceiptsBody>;
}

export const LIVE_WRITER: AppointmentWriter = {
  draft: postAppointmentsDraft,
  draftStage: postAppointmentsDraftStage,
  clearDrafts: postAppointmentsClearDrafts,
  create: postAppointmentSlot,
  patch: patchAppointmentSlot,
  withdraw: deleteAppointmentSlot,
  confirm: postAppointmentsConfirm,
  lock: postAppointmentsLock,
  unlock: postAppointmentsUnlock,
  readSchedule: fetchAppointmentsSchedule,
  readDay: fetchAppointments,
  preparePush: prepareExtensionPush,
  reportPush: reportExtensionPush,
  releasePush: releaseExtensionPush,
  preparePushDay: prepareExtensionDayPush,
  readPushReceipts: fetchAppointmentPushReceipts,
};

export interface AppointmentsPanelProps {
  scope: AppointmentScope;
  /**
   * The day as fetched. Held as seed state, which is safe because the page mounts this
   * component under a key of the scope — a different day is a different instance.
   */
  initialView: AppointmentsViewBody;
  /** The roster, for the slot picker. Empty when it could not be loaded; the screen says so. */
  officials: readonly OfficialView[];
  /**
   * What the one-press stage draft is called, worked out from the schedule by the page above
   * (`stageDraftLabel`). Defaulted so a test or a caller without the schedule still renders a
   * truthful button rather than none.
   */
  stageLabel?: string;
  /**
   * The competitions this tournament runs, from the whole schedule rather than from the day
   * on screen — a stage draft crosses every day, so the day in front of the TD is the wrong
   * place to learn what competitions exist.
   *
   * Two of them and the two bulk controls ask which one is meant; one of them and they say
   * so and ask nothing, because a choice with one answer is a question that wastes a press.
   */
  competitions?: readonly TmsCompetitionId[];
  /**
   * RUN 37 — the WHOLE schedule's appointments, for the Fixture List to list.
   *
   * *"I want to see all the matches in the one table, by scrolling up and down — previous
   * days, today, future days."* (Technical Delegate, 30 August 2026.) The page therefore reads
   * `GET /appointments` a second time with no `day` on it — the same route, which has always
   * treated `day` as optional, and no new API surface — and hands the answer down here.
   *
   * **It widens what is LISTED and what is COUNTED; it does not widen the day.** `initialView`
   * is still one day, and it is still what the day counts, the day warnings, the four bulk
   * controls and `Confirm Day` are computed from and aimed at. Where a fixture appears in
   * both, the day's copy wins: it is the one the server has just recomputed after an edit, and
   * the schedule copy is as old as the page.
   *
   * RUN 39 — and it is now WRITTEN to as well as read. A slot appointed on a match off the
   * working day lands here (`landSlotView`) rather than on `initialView`'s day, which is
   * exactly what lets the list be edited end to end without the dropdown moving underneath it.
   *
   * Absent — as in every test that renders this panel on its own — the list shows the day, as
   * it always did.
   */
  scheduleView?: AppointmentsViewBody | null;
  /**
   * The instant "today" is reckoned from, for the list's landing position.
   *
   * A parameter with a default rather than a call to the clock inside the logic, which is the
   * pattern this codebase already uses for a testable now (`todayIso` on the Fixtures page).
   */
  now?: Date;
  writer?: AppointmentWriter;
  /**
   * §A10's Export, behind a seam for the same reason the five writes are: a page test drives
   * the button and asserts the filename and the workbook it was handed, and the builders are
   * proven separately against a real `.xlsx`. The default loads `exceljs` on the press.
   */
  exporter?: WorkbookExporter;
  /**
   * RUN 84 - the TMS Push extension, behind the one module that touches `chrome.runtime`
   * (`tmsPushExtension.ts`). A seam for the reason the writes are one: a test hands in a fake
   * extension; the page has the live bridge, on the production extension's stable id.
   */
  extension?: TmsPushBridge;
  /** RUN 84 - where the tab remembers a press across a reload. `sessionStorage` by default. */
  pushMemory?: TmsPushMemory;
  /**
   * The day push (section 6c) - where the tab remembers a day push's presses across a reload, each
   * forgotten on its own. `sessionStorage` by default, under a key of its own.
   */
  pushDayMemory?: TmsDayPushMemory;
  /** RUN 84 - how often a running push is asked about again, and the margin after its hold. */
  pushTiming?: Partial<TmsPushTiming>;
}

/** What the two bulk controls are aimed at. `all` is every competition at once. */
export type BulkCompetition = "all" | TmsCompetitionId;

/** How a competition names itself in the two dialogs. Matches the API's own wording. */
export function bulkCompetitionWord(choice: BulkCompetition): string {
  if (choice === "all") return "both competitions";
  return choice === "1917" ? "the Women's competition" : "the Men's competition";
}

/** Which write a picked official is, and what it needs to address the slot. */
type SlotTarget =
  | { kind: "edit"; appointmentId: string }
  | { kind: "create"; tmsMatchDbId: TmsMatchDbId; slot: TechnicalSlot };

/** The §A7 second step, held while the TD decides. */
interface OverridePrompt {
  target: SlotTarget;
  row: string;
  /** RUN 82G — the match, so the answer can hand the focus back to the slot's own name. */
  tmsMatchDbId: TmsMatchDbId;
  tmsPersonId: string;
  slot: TechnicalSlot;
  officialName: string;
  officialCountry: string;
  displayMatchNo: string;
  teams: string;
  /** The API's own refusal, shown verbatim rather than paraphrased. */
  message: string;
  /** RUN 82D — the match's own day, to re-read if the second step finds the slot held. */
  date: string;
}

interface WithdrawPrompt {
  appointmentId: string;
  row: string;
  /** RUN 82G — the match, so `Cancel` and the answer can hand the focus back into its row. */
  tmsMatchDbId: TmsMatchDbId;
  slot: TechnicalSlot;
  officialName: string;
  displayMatchNo: string;
  /**
   * RUN 82D — the match's own day, kept from the row that opened the question for the reason
   * `UnlockPrompt.date` is kept: an `appointment_held` refusal carries no view, and it is the day
   * that has to be re-read.
   */
  date: string;
}

/** RUN 82 — the §A11 Unlock question, held while the TD decides. One match, by its TMS id. */
interface UnlockPrompt {
  tmsMatchDbId: TmsMatchDbId;
  displayMatchNo: string;
  /**
   * RUN 82A — the match's own day, kept from the row that opened the question. A refusal carries
   * no view, so this is the only way to know which day to re-read when the server says the match
   * has already moved on.
   */
  date: string;
}

/**
 * RUN 82A — where keyboard focus goes once the render a §A11 question's answer caused has landed
 * (WCAG 2.4.3).
 *
 * Held as state and acted on in an effect, never focused on the spot: the element named does not
 * exist yet when the answer is given — the note and the failure strip are drawn by the render the
 * answer causes — and the button that opened the question may be gone or disabled by then.
 * `element` is a DOM id; every request is a fresh object, so asking twice focuses twice.
 *
 * RUN 82G (QA pass 3) — every question on the page asks for one now, not only the §A11 pair: the
 * older questions' `Cancel` and answers left the focus on the page body, and a keyboard TD's next
 * Tab went to the masthead. `element` names the press that asked, the slot an answer changed, or
 * the `Clear Drafts` strip — which is why it is no longer called `opener`.
 *
 * RUN 82I (QA pass 4) — `ids`, in order of preference: the first that is on the page and can take
 * the focus wins, and where none can — the row was filtered out of view, or the slot's name went
 * read-only — the notice, then the failure strip. A slot's answer names its name and then its
 * refusal. The `Clear Drafts` strip is no longer a target: that press writes a notice now.
 *
 * RUN 82M (QA pass 6) — `refusal`, on a slot's answer that was refused: where none of `ids` is on
 * the page (a Day Warnings filter took the row out of the list), the refusal is put on the failure
 * strip there and then — once, as any other press's failure is — and the focus follows it. 82K
 * worked it out on every render instead, from the slot's refusal, which outlived the press: a
 * later draft, lock or tab change put a stale `Not Done` beside a write that had succeeded.
 */
type FocusRequest =
  | { kind: "element"; ids: readonly string[]; refusal?: string }
  | { kind: "note" }
  | { kind: "failure" };

interface PickerState {
  fixture: AppointmentFixtureBody;
  slot: AppointmentSlotBody;
  anchor: HTMLElement;
}

/** What the busy control is, so exactly one thing can be pending at a time. */
type Pending =
  | { kind: "draft" }
  | { kind: "stage" }
  | { kind: "redraft" }
  | { kind: "clear" }
  | { kind: "confirm" }
  | { kind: "lock" }
  | { kind: "unlock"; tmsMatchDbId: TmsMatchDbId }
  | { kind: "slot"; row: string }
  /** RUN 84 - an extension press, from `Confirm Push` (or a recovery on load) to its answer. */
  | { kind: "push"; competitionId: TmsCompetitionId }
  /** The day push (section 6c): every competition of the day, from `Confirm Push` to its answer. */
  | { kind: "push-day" }
  | null;

type WarningFocus = "hard" | "attention" | "advisory" | "anomaly" | null;

/** The refusal that means "you have not taken the second step yet" (§A7). */
export function isCountryConflictRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === "appointment_rule_violation" &&
    error.message.includes("acceptCountryConflict")
  );
}

/**
 * RUN 82A — the two `Unlock` refusals that mean the match changed under the screen (§A11):
 * nothing is locked on it any more (`appointment_not_locked`), or TMS has marked it Official
 * (`appointments_frozen`). Either way the row that offered the press was drawn from an older
 * day than the server's, which is why these two — and only these — are followed by a re-read.
 */
export function isMovedOnRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "appointment_not_locked" || error.code === "appointments_frozen")
  );
}

/**
 * RUN 82D (QA pass 2) — the refusal that means a slot moved on under an edit or a withdrawal
 * (§A11a): it was signed off, locked, pushed or reopened since the row was drawn, and `PATCH` or
 * `DELETE` left it alone (`appointment_held`).
 *
 * RUN 82E — and its twin on the fill path: `appointment_slot_filled`, a `POST` onto a slot that
 * somebody filled after the row was drawn. The empty row would go on offering the same fill.
 *
 * The row still offers the press the server just refused, so a retry is the same 409 again. It is
 * followed, as `isMovedOnRefusal` is for `Unlock`, by a quiet re-read of the slot's day.
 */
export function isHeldRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "appointment_held" || error.code === "appointment_slot_filled")
  );
}

/**
 * RUN 37 — the `Match day …` clause of the export's scope line, for a list that may now span
 * a fortnight.
 *
 * One day is named, several are given as a range, and none — an export of a filter that
 * matched nothing — falls back to whatever day the page is on rather than printing a clause
 * about no days at all.
 */
export function exportedDays(
  fixtures: readonly AppointmentFixtureBody[],
  fallbackDay: string | null,
): string | null {
  const days = [...new Set(fixtures.map((fixture) => fixture.date))].sort();
  if (days.length === 0) return fallbackDay === null ? null : `Match day ${fallbackDay}`;
  if (days.length === 1) return `Match day ${days[0] ?? ""}`;
  return `Match days ${days[0] ?? ""} to ${days[days.length - 1] ?? ""}`;
}

/**
 * RUN 37 — what one press of the stage draft reached, in one sentence.
 *
 * The Technical Delegate pressed `Draft The Pool Stage`, the engine wrote all ten pool days,
 * and the screen he was looking at showed him one day — so he reported that only day one had
 * drafted. The per-day strip has always been below, but a strip is read *after* you already
 * suspect something; the notice is read first, and it was silent.
 *
 * Every number here is the response's own. Days left alone and days that refused are named
 * rather than folded into the total, for the same reason the strip names them: a press that
 * skipped a signed-off day and a press that failed on one are not the same press, and an
 * average of ten days hides exactly the one the TD needs.
 */
export function stageCoverageNote(label: string, stage: StageDraftResponseBody): string {
  const totals = stage.totals;

  const parts = [
    `${countOf(totals.daysDrafted, "day")} drafted`,
    `${countOf(totals.drafted, "slot")} written`,
  ];
  if (totals.daysSkipped > 0) parts.push(`${countOf(totals.daysSkipped, "day")} left alone`);
  if (totals.daysFailed > 0) parts.push(`${countOf(totals.daysFailed, "day")} not drafted`);

  // `countOf` and not a local pluraliser: this sentence is where `60 matchs` reached the
  // screen, and `match`, `breach` and every other sibilant noun on this page now take their
  // `es` from one place. See `vocabulary.ts`.
  return (
    `${label} · ${parts.join(" · ")}. ` +
    `${countOf(totals.slotsFilled, "slot")} of ${String(totals.slots)} are now filled across ` +
    `${countOf(totals.fixtures, "match")}, with ` +
    `${countOf(totals.ruleBreaches, "rule breach")}.`
  );
}

/**
 * RUN 82I (QA pass 4) — what one press of `Clear Drafts` did, in the one sentence the notice holds:
 * `Clear Drafts · 12 slots cleared across 2 days · 3 left alone.` Days that refused are named as
 * such, as the stage draft's sentence names them. Every number is the reply's own.
 */
export function clearNotice(clear: ClearDraftsResponseBody): string {
  // RUN 82K (QA pass 5) — a scope that named no match day says so, as the strip does, rather than
  // counting nothing across nothing; and what was left alone is counted in SLOTS, by name, because
  // beside "across 1 day" a bare `9 left alone` read as nine days.
  if (clear.days.length === 0) return "Clear Drafts · no match day in that scope.";
  const totals = clear.totals;
  const parts = [
    `${countOf(totals.cleared, "slot")} cleared across ${countOf(totals.daysCleared, "day")}`,
  ];
  if (totals.leftAlone > 0) parts.push(`${countOf(totals.leftAlone, "slot")} left alone`);
  if (totals.daysFailed > 0) parts.push(`${countOf(totals.daysFailed, "day")} not cleared`);
  return `Clear Drafts · ${parts.join(" · ")}.`;
}

/**
 * RUN 82A — whether `Mark Day Locked` has anything left to do on these matches (§A11).
 *
 * A match is settled for the lock when it is locked already, or when TMS marked it Official
 * before it was ever confirmed here: the route skips that match and names it rather than refusing
 * the day, so no press of this button will ever lock it. A CONFIRMED Official match is not
 * settled — the lock takes it like any other. And a day with no matches is never settled: `every`
 * of nothing is true, and a button that said `Day Locked` over an empty day would be the screen
 * claiming a record nobody made.
 *
 * One function, read by the button and by the notice, so the two cannot disagree about when a day
 * is done.
 *
 * RUN 82D (QA pass 2) — "never confirmed here" became "not confirmed here": a match reopened with
 * `Unlock` WAS confirmed once, and if TMS marks it Official before it is confirmed again, it is
 * skipped exactly as a match nobody ever confirmed is. `skippedByTheLock` is the one test, shared
 * with the question `Mark Day Locked` asks.
 */
export function nothingLeftToLock(fixtures: readonly AppointmentFixtureBody[]): boolean {
  return (
    fixtures.length > 0 &&
    fixtures.every((fixture) => fixture.locked || skippedByTheLock(fixture))
  );
}

/**
 * RUN 82D — whether `Mark Day Locked` will leave this match exactly as it is (§A11a): TMS has
 * marked it Official, its crew is not confirmed here, and it is not locked already.
 *
 * The API's own test — an Official match that still has a lock blocker — seen from the fields the
 * view carries. The lock question counts what the press covers without these, and names them,
 * so it no longer promises a lock over a match the press will skip.
 */
export function skippedByTheLock(fixture: AppointmentFixtureBody): boolean {
  return fixture.frozen && !fixture.confirmed && !fixture.locked;
}

/** `M02` · `M02 and M05` · `M02, M05 and W01` — matches named in a sentence, in the order given. */
function matchList(matches: readonly { displayMatchNo: string }[]): string {
  const names = matches.map((match) => match.displayMatchNo);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
}

/**
 * RUN 82A — the sentence `Confirm Day` and `Mark Day Locked` add when they left a match TMS has
 * marked Official exactly as it was (§A4 rule 6, §A11).
 *
 * The two presses used to refuse a whole day over one such match; now they skip it and name it.
 * A notice that counted only what was done would let the TD believe the day was complete, so the
 * skipped matches are named by number, in the reply's order. Empty when nothing was skipped, so a
 * caller can always append it.
 */
export function officialSkipNote(skipped: readonly SkippedOfficialMatchBody[]): string {
  if (skipped.length === 0) return "";
  return skipped.length === 1
    ? `${matchList(skipped)} is Official in TMS and was left as it is.`
    : `${matchList(skipped)} are Official in TMS and were left as they are.`;
}

/**
 * RUN 82D (QA pass 2) — the same sentence, said BEFORE the press, in the lock question.
 *
 * `This covers 3 matches` used to count every match on the day, the ones the lock skips among
 * them. The count is now what the press covers, and the rest are named here with the reason they
 * are left out — so the question and the notice that follows it agree about the day. Empty when
 * the lock skips nothing, and then the question does not draw it.
 */
export function lockSkipAhead(skipped: readonly { displayMatchNo: string }[]): string {
  if (skipped.length === 0) return "";
  return skipped.length === 1
    ? `${matchList(skipped)} is Official in TMS and not confirmed here, so it will be left as it is.`
    : `${matchList(skipped)} are Official in TMS and not confirmed here, so they will be left as ` +
        `they are.`;
}

/** RUN 82G — a question's `aria-describedby`, from the ids of the paragraphs it is drawing now. */
function describedBy(ids: readonly (string | null)[]): string {
  return ids.filter((id): id is string => id !== null).join(" ");
}

/** A notice and the sentence appended to it, with the space only where there is a sentence. */
function withSentence(notice: string, sentence: string): string {
  return sentence === "" ? notice : `${notice} ${sentence}`;
}

/**
 * RUN 82G (QA pass 3) — the places a Tab can land inside a question, in order.
 *
 * A group of radios is ONE stop, reached at its checked radio (or its first, where none is
 * checked), exactly as the browser tabs through it. A disabled control is no stop at all.
 */
function questionTabStops(question: HTMLElement): HTMLElement[] {
  const stops: HTMLElement[] = [];
  const groups = new Set<string>();
  for (const element of question.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
    "button, input",
  )) {
    if (element.disabled) continue;
    if (element instanceof HTMLInputElement && element.type === "radio" && element.name !== "") {
      if (groups.has(element.name)) continue;
      groups.add(element.name);
      const group = [...question.querySelectorAll<HTMLInputElement>('input[type="radio"]')].filter(
        (radio) => radio.name === element.name && !radio.disabled,
      );
      stops.push(group.find((radio) => radio.checked) ?? element);
      continue;
    }
    stops.push(element);
  }
  return stops;
}

/** Whether the focus is on this stop — for a radio group, on any radio of it. */
function isOnStop(active: Element | null, stop: HTMLElement): boolean {
  if (active === stop) return true;
  return (
    active instanceof HTMLInputElement &&
    stop instanceof HTMLInputElement &&
    active.type === "radio" &&
    stop.type === "radio" &&
    active.name !== "" &&
    active.name === stop.name
  );
}

/**
 * RUN 82D (QA pass 2) — Tab and Shift+Tab kept on a §A11 question's own two buttons, which is
 * what its `aria-modal` promises a keyboard. Exported for its own test (RUN 84, H1).
 *
 * `inert` takes the workbench behind the question out of the tab order; what it leaves is the rest
 * of the page, which a Tab from the question's last button used to reach. So the last and first
 * buttons hand the focus to each other, and every other Tab is the browser's own. Only a button
 * that can take the focus counts: while a write is out the answer is disabled, and Tab then stays
 * on `Cancel`. There is deliberately no Escape — the frame's only ways out are its two answers.
 *
 * RUN 82G (QA pass 3) — every question on the page is kept the same way, and three of them open
 * with a choice of scope or competition. So the ends are the question's first and last TAB STOPS
 * (`questionTabStops`), not its first and last buttons: counted by buttons, Shift+Tab from `Clear
 * Them` would have wrapped to `Cancel`, and the choice above it could never be reached from the
 * keyboard at all.
 */
export function keepTabInside(event: React.KeyboardEvent<HTMLElement>): void {
  if (event.key !== "Tab") return;
  const stops = questionTabStops(event.currentTarget);
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (first === undefined || last === undefined) {
    // RUN 84 - a question with no stop of its own (the push, while it waits on the TMS Push
    // extension) keeps the focus where it is, on the question itself: Tab has nowhere to go.
    if (event.currentTarget.hasAttribute("tabindex")) event.preventDefault();
    return;
  }
  const active = document.activeElement;
  // RUN 84 (frontend review H1) - the focus is inside the question but on none of its stops: on
  // the question's own frame (it is focusable, `tabIndex={-1}`, in every phase of the push), or on
  // its answer's sentence, after a click on its words or as the answer arrives. The browser's own
  // Tab would leave from there - Shift+Tab onto the page behind the scrim, where changing the day
  // throws the open answer away. So the question's own end takes it: its first stop forwards, its
  // last backwards.
  if (!stops.some((stop) => isOnStop(active, stop))) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  const wrapTo = event.shiftKey
    ? isOnStop(active, first)
      ? last
      : null
    : isOnStop(active, last)
      ? first
      : null;
  if (wrapTo === null) return;
  event.preventDefault();
  wrapTo.focus();
}

/** What one press of `Mark Day Locked` leaves on screen: a notice, or a failure said as one. */
export interface LockOutcome {
  kind: "note" | "failure";
  text: string;
}

/**
 * RUN 82 — what one press of `Mark Day Locked` did, in one sentence (§A11).
 *
 * Every number is the reply's own and counts what THIS press locked, as `Confirm Day`'s notice
 * counts what it signed off. After a correction the TD locks a day again on which one match was
 * reopened, and `1 match · 3 appointments` is the honest answer: the day's six would claim a
 * press that did six times what it did. A press that locked nothing says exactly that, because
 * the route answers a day already locked with zero rather than a refusal, and `Day locked · 0
 * matches` would read as a lock that had quietly failed.
 *
 * RUN 82A (QA pass 1) — but "nothing new to lock" is only true of a day that is DONE, and it used
 * to be said of every reply that locked nothing. It is said now only when the reply's own day
 * passes {@link nothingLeftToLock}; a reply that locked nothing on a day that is not done is a
 * press that failed without a refusal, and is reported as a failure — with nothing claimed about
 * what the server holds, beyond that this press changed none of it. Where Official matches were
 * skipped, "every appointment" becomes "every other appointment", and the skipped are named.
 *
 * RUN 82D (QA pass 2) — and "already locked" is only true of a day on which something IS locked.
 * On a page left open while every match on the day went Official unconfirmed, the press locks
 * nothing, skips everything, and the day is done with no record made at all — the day the button
 * calls `Nothing To Lock`. The notice says the same: `Nothing to lock on {day}.`, then names the
 * skipped. A reply from an API older than RUN 82A carries no `skippedOfficial`, and is read as
 * nothing skipped rather than thrown over.
 */
export function lockNotice(reply: LockResponseBody, day: string): LockOutcome {
  const skipped = officialSkipNote(reply.skippedOfficial ?? []);
  if (reply.lockedSlots > 0) {
    return {
      kind: "note",
      text: withSentence(
        `Day locked · ${countOf(reply.lockedFixtures.length, "match")} · ` +
          `${countOf(reply.lockedSlots, "appointment")} marked On TMS.`,
        skipped,
      ),
    };
  }
  if (!nothingLeftToLock(reply.fixtures)) {
    return {
      kind: "failure",
      text:
        `Nothing was locked on ${dayLabel(day)}, although the day is not fully locked. Nothing ` +
        `was changed — reload the page and try again.`,
    };
  }
  if (!reply.fixtures.some((fixture) => fixture.locked)) {
    return { kind: "note", text: withSentence(`Nothing to lock on ${dayLabel(day)}.`, skipped) };
  }
  return {
    kind: "note",
    text: withSentence(
      `Nothing new to lock — every ${skipped === "" ? "" : "other "}appointment on ` +
        `${dayLabel(day)} is already locked.`,
      skipped,
    ),
  };
}

function teamsOf(fixture: AppointmentFixtureBody): string {
  return `${fixture.homeTeamCode ?? "TBC"} v ${fixture.awayTeamCode ?? "TBC"}`;
}

/** Which severity bucket a warning falls in, as the day summary counts them. */
function bucketOf(warning: AppointmentWarning): Exclude<WarningFocus, null> {
  if (isAnomaly(warning)) return "anomaly";
  if (warning.severity === "hard") return "hard";
  if (warning.severity === "attention") return "attention";
  return "advisory";
}

/**
 * The optimistic cell — the picked official in place, pending the server's own answer.
 *
 * Addressed by fixture and slot rather than by appointment id so that the same function
 * covers a slot being filled for the first time, which has no id to be addressed by. The id
 * is left exactly as it was: a created row's id is the server's to issue, and inventing one
 * here would put a value on screen that nothing could ever be written against.
 */
function withOptimisticSlot(
  view: AppointmentsViewBody,
  tmsMatchDbId: TmsMatchDbId,
  target: TechnicalSlot,
  official: OfficialView,
): AppointmentsViewBody {
  return {
    ...view,
    fixtures: view.fixtures.map((fixture) => ({
      ...fixture,
      slots: fixture.slots.map((slot) =>
        fixture.tmsMatchDbId === tmsMatchDbId && slot.slot === target
          ? {
              ...slot,
              tmsPersonId: official.tmsPersonId,
              mediaName: official.mediaName,
              country: official.country,
              role: official.role,
              origin: "td_manual" as const,
              status: "td_modified" as const,
            }
          : slot,
      ),
    })),
  };
}

/**
 * RUN 39 — fold ONE day's freshly-recomputed view into the schedule-wide listing.
 *
 * The five per-slot writes answer with a view of the EDITED fixture's own day (the API builds
 * it from `fixture.date`), which until now was simply adopted as the working day. That was the
 * seam that made every row off the working day read-only: an edit to tomorrow's match would
 * have swapped the panel's day out from under the Match Day dropdown, the day-warning counts
 * and `Confirm Day`.
 *
 * The seam is closed here rather than in the API. A reply about another day is folded into the
 * backdrop — the fixtures it names replace their older copies, and so do the warnings that name
 * them — and the working day is left exactly as it was. Nothing about the write path changes;
 * what changes is which piece of client state the answer lands on.
 *
 * Warnings are replaced by FIXTURE, not wholesale: the backdrop carries findings about every
 * other day and the tournament-wide pair advisories, and a reply about one day has nothing to
 * say about any of them. Day-wide warnings in the reply (`tms_match_db_id === null`) are
 * dropped for the same reason — they are that day's, and the schedule read is what re-derives
 * the tournament's own.
 */
export function foldDayIntoSchedule(
  base: AppointmentsViewBody,
  day: AppointmentsViewBody,
): AppointmentsViewBody {
  const fresh = new Map(day.fixtures.map((fixture) => [fixture.tmsMatchDbId, fixture]));
  return {
    ...base,
    fixtures: base.fixtures.map((fixture) => fresh.get(fixture.tmsMatchDbId) ?? fixture),
    warnings: [
      ...base.warnings.filter(
        (warning) =>
          warning.tms_match_db_id === null || !fresh.has(warning.tms_match_db_id),
      ),
      ...day.warnings.filter(
        (warning) =>
          warning.tms_match_db_id !== null && fresh.has(warning.tms_match_db_id),
      ),
    ],
  };
}

/** `2026-09-18` → `18 Sep`. The strip has ten of these on it and needs every character back. */
function shortDay(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return `${String(parsed.getUTCDate())} ${
    ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
      parsed.getUTCMonth()
    ] ?? ""
  }`;
}

/**
 * What one press of the stage draft did, day by day: `18 Sep 12/12 · 19 Sep 12/12 · 20 Sep
 * 8/8 — 0 rule breaches`.
 *
 * Compact on purpose, and per-day on purpose. A stage draft that touched ten days cannot be
 * honestly reduced to one number — a day that was skipped because it is already signed off
 * and a day that refused are both invisible in a total — so each day gets its own figure and
 * says which of the three things it was.
 */
function StageResultStrip({ stage }: { stage: StageDraftResponseBody }): React.JSX.Element {
  const dayLabel = (day: StageDraftDayBody): string => {
    if (day.outcome === "failed") return "not drafted";
    if (day.outcome === "skipped") return "left alone";
    return `${String(day.slotsFilled)}/${String(day.slots)}`;
  };

  return (
    <div className="stagestrip" role="status" data-testid="stage-result">
      <span className="kicker">Stage Draft</span>
      <span className="stagestrip__days">
        {stage.days.map((day, index) => (
          <span key={day.day}>
            {index === 0 ? null : <span className="stagestrip__sep"> · </span>}
            <span
              className={`stagestrip__day stagestrip__day--${day.outcome}`}
              title={day.reason ?? `${String(day.drafted)} slots written`}
              data-testid={`stage-day-${day.day}`}
            >
              {shortDay(day.day)} {dayLabel(day)}
            </span>
          </span>
        ))}
      </span>
      <span
        className={`stagestrip__verdict${stage.totals.ruleBreaches === 0 ? "" : " is-bad"}`}
        data-testid="stage-breaches"
      >
        — {stage.totals.ruleBreaches} rule breach
        {stage.totals.ruleBreaches === 1 ? "" : "es"}
      </span>
      <span className="stagestrip__detail">
        {stage.totals.daysDrafted} day{stage.totals.daysDrafted === 1 ? "" : "s"} drafted
        {stage.totals.daysSkipped === 0 ? "" : `, ${String(stage.totals.daysSkipped)} left alone`}
        {stage.totals.daysFailed === 0 ? "" : `, ${String(stage.totals.daysFailed)} not drafted`} ·{" "}
        {stage.totals.drafted} slot{stage.totals.drafted === 1 ? "" : "s"} written ·{" "}
        {stage.totals.redraftPasses} redraft pass
        {stage.totals.redraftPasses === 1 ? "" : "es"} ·{" "}
        {stage.balanced
          ? "every judge inside the scoring/timing tolerance"
          : "an unresolvable scoring/timing spread remains — see the day warnings"}
      </span>
    </div>
  );
}

/**
 * What one press of `Clear Drafts` did, day by day: `18 Sep 12 cleared · 19 Sep 3 left alone
 * · 20 Sep nothing to clear — 24 slots cleared, 3 left alone`.
 *
 * Per day and not a total, for the same reason the stage draft's strip is: a day that was
 * skipped because it is already signed off is invisible in an average, and it is exactly the
 * day the TD needs to know about.
 */
function ClearResultStrip({ clear }: { clear: ClearDraftsResponseBody }): React.JSX.Element {
  const dayLabel = (day: ClearDraftsDayBody): string => {
    if (day.outcome === "failed") return "not cleared";
    if (day.outcome === "skipped") return day.leftAlone === 0 ? "nothing to clear" : `${String(day.leftAlone)} left alone`;
    return `${String(day.cleared)} cleared${day.leftAlone === 0 ? "" : `, ${String(day.leftAlone)} left alone`}`;
  };

  return (
    <div className="stagestrip stagestrip--clear" role="status" data-testid="clear-result">
      <span className="kicker">Clear Drafts</span>
      <span className="stagestrip__days">
        {clear.days.length === 0 ? (
          <span className="stagestrip__day stagestrip__day--skipped">
            No match day in that scope
          </span>
        ) : null}
        {clear.days.map((day, index) => (
          <span key={day.day}>
            {index === 0 ? null : <span className="stagestrip__sep"> · </span>}
            <span
              className={`stagestrip__day stagestrip__day--${day.outcome}`}
              title={day.reason ?? `${String(day.cleared)} slots emptied`}
              data-testid={`clear-day-${day.day}`}
            >
              {shortDay(day.day)} {dayLabel(day)}
            </span>
          </span>
        ))}
      </span>
      <span className="stagestrip__verdict" data-testid="clear-totals">
        — {clear.totals.cleared} slot{clear.totals.cleared === 1 ? "" : "s"} cleared,{" "}
        {clear.totals.leftAlone} left alone
      </span>
      <span className="stagestrip__detail">
        {clear.totals.daysCleared} day{clear.totals.daysCleared === 1 ? "" : "s"} cleared
        {clear.totals.daysSkipped === 0 ? "" : `, ${String(clear.totals.daysSkipped)} with nothing to clear`}
        {clear.totals.daysFailed === 0 ? "" : `, ${String(clear.totals.daysFailed)} not cleared`} ·{" "}
        {clear.totals.leftAlone === 0
          ? "nothing was protected — the scope held no confirmed, pushed or overridden slot"
          : "confirmed, pushed and overridden slots, and every slot on a match TMS has marked Official, were left exactly as they were"}
      </span>
    </div>
  );
}

export function AppointmentsPanel({
  scope,
  initialView,
  officials,
  stageLabel = "Draft The Pool Stage",
  competitions,
  scheduleView = null,
  now,
  writer = LIVE_WRITER,
  exporter = exportWorkbook,
  extension = LIVE_TMS_PUSH_BRIDGE,
  pushMemory = SESSION_TMS_PUSH_MEMORY,
  pushDayMemory = SESSION_TMS_DAY_PUSH_MEMORY,
  pushTiming,
}: AppointmentsPanelProps): React.JSX.Element {
  const tournament = useTournament();
  const [view, setView] = useState<AppointmentsViewBody>(initialView);
  /**
   * RUN 84 - a press this tab remembers from before a reload (section 4, step 6), read once as the
   * panel mounts. While there is one, the page opens busy, with the push question saying it is
   * asking the TMS Push extension what became of it.
   */
  const [rememberedPress] = useState<RememberedPress | null>(() => pushMemory.read());
  /**
   * The day push (section 6c) - the presses of a day push this tab remembers from before a reload,
   * read once as the panel mounts: each is recovered as section 6b recovers one. A single press
   * remembered as well - which one push at a time never leaves - goes first, exactly as it always
   * did, and the day push's wait for the next load.
   */
  const [rememberedDay] = useState<readonly RememberedPress[]>(() =>
    rememberedPress === null ? pushDayMemory.read() : [],
  );
  /**
   * The whole schedule, as the backdrop the working day is drawn on. Held as state rather than
   * read straight off the prop because a press that crosses days — the stage draft, a
   * stage-wide clear — makes the page's copy of it out of date the moment it succeeds, and the
   * backdrop has to be re-read or the TD is shown nine stale days beside one fresh one.
   */
  const [schedule, setSchedule] = useState<AppointmentsViewBody | null>(scheduleView);
  const [pending, setPending] = useState<Pending>(() =>
    rememberedPress !== null
      ? { kind: "push", competitionId: rememberedPress.competitionId }
      : rememberedDay.length > 0
        ? { kind: "push-day" }
        : null,
  );
  /**
   * RUN 84 - the push question, in whichever of its moments it is (`TmsPushFlowState`), or null.
   * A remembered press opens it at once, recovering.
   */
  const [pushFlow, setPushFlow] = useState<TmsPushFlowState | null>(() =>
    rememberedPress === null
      ? null
      : {
          kind: "working",
          competitionId: rememberedPress.competitionId,
          day: rememberedPress.day,
          step: "recovering",
          press: rememberedPress,
          attempt: 1,
          attempts: 1,
        },
  );
  /**
   * The day push's question (section 6c), in whichever of its moments it is, or null. A remembered
   * day push opens it at once, recovering.
   */
  const [dayFlow, setDayFlow] = useState<TmsDayPushFlowState | null>(() => {
    const first = rememberedDay[0];
    return first === undefined
      ? null
      : {
          kind: "working",
          day: first.day,
          step: "recovering",
          press: first,
          hold: rememberedDay.find((press) => press.holdUntil !== null) ?? null,
          attempt: 1,
          attempts: 1,
          ended: [],
        };
  });
  /** RUN 84 - the TMS Push extension as the page last found it: looked for once, as it mounts. */
  const [presence, setPresence] = useState<PushPresenceState>(() =>
    extension.reachable() ? "checking" : "absent",
  );
  /**
   * RUN 84 - a press the tab still remembers once its question has closed: its result is kept and not
   * recorded yet, and no new push starts until a reload has recorded it (the new one would take its
   * place in the tab's memory).
   */
  const [unrecordedPress, setUnrecordedPress] = useState<RememberedPress | null>(null);
  /**
   * RUN 84 - what the last reported push left on the page, once its question was closed. *Day push
   * (section 6c):* one strip per competition a day push reported - a single press leaves one, as before.
   */
  const [pushStrips, setPushStrips] = useState<readonly TmsPushStripState[]>([]);
  /** RUN 84 - false once the panel has gone: a recovery that is only waiting then stops. */
  const mountedRef = useRef(true);
  /** RUN 84 - the recovery of a remembered press is started once, whatever StrictMode replays. */
  const recoveryStarted = useRef(false);
  /** The day push's recovery on mount, started once for the same reason. */
  const dayRecoveryStarted = useRef(false);
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [slotError, setSlotError] = useState<{ row: string; message: string } | null>(null);
  const [override, setOverride] = useState<OverridePrompt | null>(null);
  const [withdraw, setWithdraw] = useState<WithdrawPrompt | null>(null);
  /** RUN 82 — the two §A11 questions: `Mark Day Locked` for the day, `Unlock` for one match. */
  const [lockPrompt, setLockPrompt] = useState(false);
  const [unlockPrompt, setUnlockPrompt] = useState<UnlockPrompt | null>(null);
  /** RUN 82A — the focus hand-off the last §A11 answer asked for. See `FocusRequest`. */
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const noteRef = useRef<HTMLParagraphElement | null>(null);
  const failureRef = useRef<HTMLDivElement | null>(null);
  const lockCancelRef = useRef<HTMLButtonElement | null>(null);
  const unlockCancelRef = useRef<HTMLButtonElement | null>(null);
  /**
   * RUN 82G (QA pass 3) — every question this panel draws, and nothing else on the page. The focus
   * that goes into an opening question is looked for HERE: looked for across the document, it found
   * the masthead's own `Sign Out` confirmation first whenever that was open, and put a keyboard TD
   * one Tab from `Sign Out Anyway`.
   */
  const questionsRef = useRef<HTMLDivElement | null>(null);
  const [picker, setPicker] = useState<PickerState | null>(null);
  const [pulsing, setPulsing] = useState<string | null>(null);
  const [warningFocus, setWarningFocus] = useState<WarningFocus>(null);
  /** The stage draft's two moments: the confirmation it asks for, and the strip it leaves. */
  const [stagePrompt, setStagePrompt] = useState(false);
  const [stageResult, setStageResult] = useState<StageDraftResponseBody | null>(null);
  /** `Re-Draft` asks the same kind of question the stage draft does, in its own words. */
  const [redraftPrompt, setRedraftPrompt] = useState(false);
  /**
   * RUN 38 — which shuffle of the stage draft the page is on.
   *
   * 0 is the plain `Draft The Pool Stage`, which sends no offset at all. Each `Re-Draft` press
   * sends `variant + 1` and increments, so the first press is variant 1, the second variant 2,
   * and so on.
   *
   * **It is page-session state and a reload starts again at variant 0.** That is stated here
   * rather than worked around, because the behaviour it produces is the one wanted: the
   * engine is deterministic in the offset, so a re-draft after a reload reproduces variant 1
   * exactly — the same draft, not a new one — and the Technical Delegate keeps whichever
   * variant he likes by the simple act of not pressing this again. There is no "variant 3 of
   * yesterday" to go back to and there is deliberately nothing pretending otherwise: what is
   * in the database is the draft, and the counter only says how many shuffles produced it in
   * this sitting.
   */
  const [variant, setVariant] = useState(0);
  /** `Clear Drafts`: the question with its scope choice, and the strip it leaves. */
  const [clearPrompt, setClearPrompt] = useState<"day" | "stage" | null>(null);
  const [clearResult, setClearResult] = useState<ClearDraftsResponseBody | null>(null);
  /**
   * Which competition the two bulk controls are aimed at.
   *
   * It is asked in the dialog rather than taken from a panel's competition tab: the tabs
   * narrow what is DISPLAYED on one day and this decides what is WRITTEN across a fortnight,
   * and a control that quietly inherited a display filter would let a tab pressed to read
   * something change what the next press does to the database.
   */
  const [bulkComp, setBulkComp] = useState<BulkCompetition>("all");

  /* Per-panel filters — four competition tabs, one stage tab, three officials tabs. */
  const [fixComp, setFixComp] = useState<CompetitionFilter>("all");
  const [fixStage, setFixStage] = useState<StageFilter>("all");
  const [matComp, setMatComp] = useState<CompetitionFilter>("all");
  const [matRole, setMatRole] = useState<RoleFilter>("all");
  const [techComp, setTechComp] = useState<CompetitionFilter>("all");
  const [techRole, setTechRole] = useState<RoleFilter>("all");
  const [tfComp, setTfComp] = useState<CompetitionFilter>("all");
  const [tfRole, setTfRole] = useState<RoleFilter>("all");

  const workbenchRef = useRef<HTMLDivElement | null>(null);

  /* The page's read of the schedule arrives a moment after the day's does, and again if the
     page ever re-reads it. Adopted whenever it changes; a re-read this component made itself
     is not overwritten, because the prop only changes when the page has something newer. */
  useEffect(() => {
    if (scheduleView !== null) setSchedule(scheduleView);
  }, [scheduleView]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * RUN 84 - look for the TMS Push extension as the panel mounts (`12-tms-push-extension.md`
   * section 4, step 1), so each push control can say at once what to expect. Where there is no
   * runtime the answer is known without asking and nothing is sent. The press asks again for itself
   * - for three seconds - before it prepares anything, and the controls stay pressable while the
   * extension has not answered (frontend review M2): one slow answer from a sleeping worker is not
   * "not installed".
   */
  useEffect(() => {
    if (!extension.reachable()) return;
    let live = true;
    extension.hello().then(
      (answer) => {
        if (live) setPresence(answer.kind === "present" ? "present" : "absent");
      },
      () => {
        if (live) setPresence("absent");
      },
    );
    return () => {
      live = false;
    };
  }, [extension]);

  /**
   * RUN 84 (frontend review M2) - look again whenever this window regains the focus, as the TD comes
   * back from installing, enabling or allowing the extension: the controls' note follows what it
   * says. The one-second look, never while a write is out, and the controls are never locked by it.
   */
  useEffect(() => {
    if (pending !== null) return;
    let live = true;
    function lookAgain(): void {
      if (!extension.reachable()) {
        setPresence("absent");
        return;
      }
      extension.hello().then(
        (answer) => {
          if (live) setPresence(answer.kind === "present" ? "present" : "absent");
        },
        () => {
          if (live) setPresence("absent");
        },
      );
    }
    window.addEventListener("focus", lookAgain);
    return () => {
      live = false;
      window.removeEventListener("focus", lookAgain);
    };
  }, [extension, pending]);

  /**
   * RUN 84 - a press this tab remembered from before a reload (section 4, step 6): the TMS Push
   * extension is asked what became of it, a result it kept is reported, and the TD is told what
   * happened - or, where it kept nothing and the hold has lapsed, that the push may not have
   * happened and TMS is where to look. Started once for the mount: the recovery is a press of its
   * own, and one is enough.
   */
  useEffect(() => {
    const press = rememberedPress;
    if (press === null || recoveryStarted.current) return;
    recoveryStarted.current = true;
    void (async () => {
      const ending = await recoverTmsPush(pushDeps(), press);
      await finishPush(ending, press.competitionId, press.day);
    })();
    // Once per mount, by design: `rememberedPress` is read once, and `recoveryStarted` holds it to
    // one run whatever StrictMode replays.
  }, []);

  /**
   * The day push (`12-tms-push-extension.md` section 6c) - the presses of a day push this tab
   * remembered from before a reload, "each recovered as 6b recovers one": the TMS Push extension asked
   * what became of each (`status`), a result it kept reported, and - before any is said to have no
   * result - the day's receipts asked for. In the send order, one press at a time. Started once for
   * the mount, as the single press's recovery is.
   */
  useEffect(() => {
    const presses = rememberedDay;
    const first = presses[0];
    if (first === undefined || dayRecoveryStarted.current) return;
    dayRecoveryStarted.current = true;
    void (async () => {
      const ending = await recoverTmsDayPush(dayPushDeps(), presses);
      await finishDayPush(ending, first.day);
    })();
    // Once per mount, by design, for the single press's recovery's reason.
  }, []);

  /**
   * RUN 82A — WCAG 2.4.3 for the two §A11 questions. An open question takes the focus on its
   * `Cancel`, the one answer that changes nothing, so a keyboard user who pressed a lock control
   * lands inside the question instead of behind its scrim, and an Enter pressed in haste writes
   * nothing. (The page's older questions are not changed here.)
   *
   * RUN 82G — they are now: see the effect after `openQuestion`, which takes every question there.
   */
  useEffect(() => {
    if (lockPrompt) lockCancelRef.current?.focus();
  }, [lockPrompt]);
  useEffect(() => {
    if (unlockPrompt !== null) unlockCancelRef.current?.focus();
  }, [unlockPrompt]);

  /**
   * RUN 82A — the hand-off after an answer: back to the button that asked on `Cancel`, to the
   * notice after a write that succeeded, to the failure strip after one that did not.
   *
   * Only where the focus has fallen to the page body — which is where it goes when the question
   * that held it closes. A write can take seconds, and a TD who has tabbed on to something else
   * meanwhile is not pulled back to it; the notice is announced by its live region either way.
   */
  useEffect(() => {
    if (focusRequest === null) return;
    const found =
      focusRequest.kind === "element"
        ? (focusRequest.ids
            .map((id) => document.getElementById(id))
            .find((element) => element !== null && !element.matches(":disabled")) ?? null)
        : null;
    // RUN 82M — a refusal with no row left to be drawn on is said on the failure strip, whether or
    // not the focus is still to be handed anywhere; the hand-off then follows it there.
    if (focusRequest.kind === "element" && found === null && focusRequest.refusal !== undefined) {
      setFailure(focusRequest.refusal);
      setFocusRequest({ kind: "failure" });
      return;
    }
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    const target =
      focusRequest.kind === "note"
        ? noteRef.current
        : focusRequest.kind === "failure"
          ? failureRef.current
          : (found ?? noteRef.current ?? failureRef.current);
    if (target === null) return;
    // RUN 82K (QA pass 5) — an element that takes no focus of its own (a slot's refusal) is made
    // focusable for this hand-off only, and given back to the page as it was once the focus moves on.
    if (target.tabIndex < 0 && !target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
      // RUN 82M — a blur that is the WINDOW losing focus (another app, another tab) is not the TD
      // moving on: the page's focus is still on the refusal, and comes back to it with the window,
      // so it stays focusable. Checked once the blur has finished moving the focus.
      const giveBack = (): void => {
        queueMicrotask(() => {
          if (document.activeElement === target) {
            target.addEventListener("blur", giveBack, { once: true });
            return;
          }
          target.removeAttribute("tabindex");
        });
      };
      target.addEventListener("blur", giveBack, { once: true });
    }
    target.focus();
  }, [focusRequest]);

  /**
   * Re-read the backdrop after a press that wrote across days.
   *
   * Deliberately quiet. It is a read that repaints nine days the TD is not standing on, and a
   * failure means those nine days go on showing what they showed a minute ago — which is not
   * worth a red banner over a write that succeeded. The day in front of him is authoritative
   * either way, because it came back with the write itself.
   */
  async function refreshSchedule(): Promise<void> {
    if (schedule === null || writer.readSchedule === undefined) return;
    try {
      setSchedule(await writer.readSchedule());
    } catch {
      /* The backdrop stays as it was. The working day is already correct. */
    }
  }

  /**
   * RUN 38 — the pair-concentration advisories, which belong to the tournament rather than to
   * any one day.
   *
   * They are raised ONLY on the schedule-wide `GET /appointments`, so they arrive on the
   * backdrop read and never on the day view. That is what makes counting them here safe: the
   * two sources cannot both hold the same finding, so adding the schedule's copy to the day's
   * warnings counts each pair exactly once no matter how many days it spans. Were they
   * day-scoped they would appear ten times on a ten-day tournament and the strip would say
   * `10 advisory` about one pair of officials.
   *
   * Empty until the page has read the schedule at all, which is honest: a screen that has only
   * seen one day has not seen enough to say anything about a fortnight's pairings.
   */
  const pairWarnings = useMemo<readonly AppointmentWarning[]>(
    () =>
      schedule === null
        ? []
        : schedule.warnings.filter(
            (warning) => warning.code === "co_appointment_concentration",
          ),
    [schedule],
  );

  const counts = daySummaryCounts([...view.warnings, ...pairWarnings]);
  /* Warnings that name no fixture: the day's own, and the tournament-wide pair findings, which
     name no fixture because they are about a pattern across many. */
  const dayWide = [
    ...view.warnings.filter((warning) => warning.tms_match_db_id === null),
    ...pairWarnings,
  ];

  /**
   * RUN 37 — every match in the tournament, with the working day's copy on top.
   *
   * Two sources, one row each, and the precedence is not arbitrary: the day view is what the
   * server has recomputed after every draft, edit, withdrawal and sign-off in this session,
   * and the schedule view is what it said when the page loaded. A list that preferred the
   * older of the two would put an edit back the moment it was made.
   */
  const listSource = useMemo<readonly AppointmentFixtureBody[]>(() => {
    if (schedule === null) return view.fixtures;
    const live = new Map(view.fixtures.map((fixture) => [fixture.tmsMatchDbId, fixture]));
    const merged = schedule.fixtures.map(
      (fixture) => live.get(fixture.tmsMatchDbId) ?? fixture,
    );
    // A day the schedule read did not carry at all — it cannot happen against one API, and
    // dropping the day the TD is working on if it ever did would be the worst possible way to
    // find out. Appended rather than assumed away; `dayGroups` puts it back in date order.
    const seen = new Set(merged.map((fixture) => fixture.tmsMatchDbId));
    return [...merged, ...view.fixtures.filter((f) => !seen.has(f.tmsMatchDbId))];
  }, [schedule, view]);

  /**
   * The warnings for the rows above. The working day's come from the day view; every other
   * day's from the schedule read.
   *
   * RUN 39 — the backdrop is no longer a set of rows that nothing can change. An edit made on
   * another day is folded straight into it (`landSlotView`) and the whole schedule is re-read
   * behind that, so the findings beside those rows keep pace with the rows themselves. The
   * precedence is unchanged and still matters: where a fixture appears in both, the day view's
   * warnings are the ones the server recomputed most recently for the day on screen.
   */
  const listWarnings = useMemo<readonly AppointmentWarning[]>(() => {
    if (schedule === null) return view.warnings;
    const onDay = new Set(view.fixtures.map((fixture) => fixture.tmsMatchDbId));
    return [
      ...view.warnings,
      ...schedule.warnings.filter(
        (warning) =>
          warning.tms_match_db_id !== null && !onDay.has(warning.tms_match_db_id),
      ),
    ];
  }, [schedule, view]);


  /**
   * The widened listing wearing the day view's other fields, purely so `matrixOfficials` can
   * backfill a name the roster does not carry from ALL the appointments rather than from one
   * day's. Nothing else reads it.
   */
  const listView = useMemo<AppointmentsViewBody>(
    () => ({ ...view, fixtures: [...listSource] }),
    [view, listSource],
  );

  /**
   * The day the page is APPOINTING — the one the Match Day dropdown names.
   *
   * Read from the day view's own scope, falling back to the scope the page was mounted with,
   * and deliberately independent of whether the schedule has been read: `workingDay` below is
   * this same day but `null` until there is a backdrop to be off, because a list that shows
   * one day has no "other day" to mark. The writes need the plainer question — *is this reply
   * about the day on screen?* — and that is what this answers.
   *
   * **Nothing moves it.** It follows the dropdown and the dropdown only. An edit landing from
   * another day cannot change it, which is the whole of RUN 39's guarantee.
   */
  const appointingDay = view.scope.day ?? scope.day ?? null;

  /* ── The derived tables ─────────────────────────────────────────────────── */

  const matrixTable = useMemo(
    () => buildMatrix(filterFixtures(listSource, matComp), matrixOfficials(listView, officials, matRole)),
    [listSource, listView, officials, matComp, matRole],
  );
  const techRows = useMemo(
    () => buildTech(filterFixtures(listSource, techComp), matrixOfficials(listView, officials, techRole)),
    [listSource, listView, officials, techComp, techRole],
  );
  const teamsTable = useMemo(
    () => buildTeams(filterFixtures(listSource, tfComp), matrixOfficials(listView, officials, tfRole)),
    [listSource, listView, officials, tfComp, tfRole],
  );

  const flashing = useCellFlash(
    useMemo(
      () => derivedCellValues(matrixTable, techRows, teamsTable),
      [matrixTable, techRows, teamsTable],
    ),
  );

  /**
   * RUN 39 — the day the §A6 picker measures against: the EDITED fixture's, never the
   * dropdown's.
   *
   * Every blocking and advisory test in `pickerOptions` is a question about one day — who is
   * already at this kick-off, how many appointments this official already holds *on this day*,
   * how a judge's scoring/timing split would sit *on this day*. Handed the working day's
   * fixtures while the TD edits Sunday, it would answer all three about the wrong day: an
   * official at their daily cap on Sunday would be offered without a word, and one at their cap
   * on the working day would be flagged on a match they are not appointed to.
   *
   * So the popover is given the edited fixture's own day, taken from the whole-schedule
   * listing. Where the schedule has never been read this is the day view filtered by its own
   * date, which is the same set it always was.
   */
  const pickerDayFixtures = useMemo<readonly AppointmentFixtureBody[]>(
    () =>
      picker === null
        ? []
        : listSource.filter((fixture) => fixture.date === picker.fixture.date),
    [picker, listSource],
  );

  /* ── The Fixture List's own view ────────────────────────────────────────── */

  const fixturesInScope = filterFixtures(listSource, fixComp, fixStage);
  const focusedIds = new Set(
    warningFocus === null
      ? []
      : view.warnings
          .filter((warning) => bucketOf(warning) === warningFocus && warning.tms_match_db_id !== null)
          .map((warning) => warning.tms_match_db_id as number),
  );
  const listedFixtures =
    warningFocus === null
      ? fixturesInScope
      : fixturesInScope.filter((fixture) => focusedIds.has(fixture.tmsMatchDbId));

  /* ── The writes ─────────────────────────────────────────────────────────── */

  /*
   * RUN 82D (QA pass 2) — every write below opens with `if (pending !== null) return;`, as the
   * lock layer's two have since RUN 82A. A second write started while one is out shares the one
   * `pending`, and whichever `finally` ran first cleared the busy state of the write still running:
   * that is how a `Clear Drafts` answered behind an open lock re-enabled `Mark Day Locked` while the
   * first lock was still out. Every press that reaches these but one is also disabled while
   * anything is busy, so for them the guard is the last of the layers, not the first; the one is
   * the pick, and `pickOfficial` says why.
   */

  async function runDraft(): Promise<void> {
    if (pending !== null) return;
    setPending({ kind: "draft" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    try {
      const drafted = await writer.draft(scope);
      setView(drafted);
      setNote(
        `Draft All · ${String(drafted.drafted)} slot${drafted.drafted === 1 ? "" : "s"} drafted ` +
          `in ${String(drafted.redraftPasses)} redraft pass${
            drafted.redraftPasses === 1 ? "" : "es"
          }` +
          (drafted.skipped.length === 0
            ? "."
            : ` · ${String(drafted.skipped.length)} fixture${
                drafted.skipped.length === 1 ? "" : "s"
              } left alone.`),
      );
    } catch (error) {
      setFailure(
        error instanceof ApiError ? error.message : "The draft did not run. Nothing was written.",
      );
    } finally {
      setPending(null);
    }
  }

  /**
   * §A8's *"all pool matches up front"* — the whole stage in one press.
   *
   * Two things make this different from `Draft Appointments` and both are deliberate:
   *
   *  * **It crosses days whichever day is on screen.** The scope sent names the day being
   *    looked at only so the reply can carry that day back recomputed; the draft itself runs
   *    over the whole stage. The tables below therefore refresh and flash exactly as they do
   *    for a single-day draft, while ten other days have been written behind them.
   *  * **It leaves a per-day strip rather than a sentence.** A press that touched ten days
   *    cannot be honestly summarised in one number, and the strip is where a skipped day or
   *    a day that refused is visible instead of averaged away.
   */
  async function runStageDraft(): Promise<void> {
    if (pending !== null) return;
    setStagePrompt(false);
    setPending({ kind: "stage" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    setStageResult(null);
    try {
      const stage = await writer.draftStage(bulkScope);
      setStageResult(stage);
      // RUN 37 — the press writes a fortnight, so the sentence beside it counts a fortnight.
      // The strip below already breaks it down day by day; what was missing was the one line
      // that says, in the same place every other write says it, how far the press reached.
      setNote(stageCoverageNote(stageLabel, stage));
      // RUN 82G — the question that asked is gone, so the focus goes to what the press said.
      setFocusRequest({ kind: "note" });
      // The day on screen, recomputed by the server. Null only when the day is not in the
      // stage at all — a knockout day, say — and then the tables in front of the TD are
      // still correct, because nothing on them was touched.
      if (stage.view !== null) setView(stage.view);
      await refreshSchedule();
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The stage draft did not run. Nothing was written.",
      );
      setFocusRequest({ kind: "failure" });
    } finally {
      setPending(null);
    }
  }

  /**
   * RUN 38 — `Re-Draft`. The same stage draft, shuffled.
   *
   * The Technical Delegate's first real pool-stage draft was even on every figure §A4 measures
   * and still put one Technical Officer with the same Judge four times and with two others not
   * at all. Nothing about it was wrong, and there was nothing to fix — what he wanted was
   * *another one*, equally legal, arranged differently. The engine could always give him one:
   * `rotation_offset` rotates the final tie-break order, and a different offset yields a
   * different draft that satisfies every rule the first one did. The client simply never sent
   * it.
   *
   * So this is the stage draft with a number on it, and everything else about it is unchanged
   * — including, and this is the part that makes the button safe to press, the protections.
   * A redraft keeps every confirmed slot (CC-Q25), every hand edit (§A8's *"any appointment
   * you have already modified by hand is kept"*), every pushed slot and every §A7 override,
   * because those are `isLockedAgainstRedraft` on the API side and this route is the same
   * route. Pressing it cannot lose work; it can only rearrange what the engine chose.
   *
   * The variant is sent, then banked. `variant + 1` goes out and the counter follows on the
   * way back, so a press that fails leaves the counter where it was and the next press is the
   * same variant rather than a skipped one.
   */
  async function runReDraft(): Promise<void> {
    if (pending !== null) return;
    setRedraftPrompt(false);
    const next = variant + 1;
    setPending({ kind: "redraft" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    setStageResult(null);
    try {
      const stage = await writer.draftStage(bulkScope, next);
      setStageResult(stage);
      setVariant(next);
      // The same sentence the stage draft leaves, under a label that says which shuffle this
      // was: `Re-draft · variant 2 · 10 days drafted · 180 slots written. …`. One
      // implementation of the coverage wording, so a redraft and a first draft can never
      // report the same press two different ways.
      setNote(stageCoverageNote(`Re-draft · variant ${String(next)}`, stage));
      setFocusRequest({ kind: "note" });
      if (stage.view !== null) setView(stage.view);
      await refreshSchedule();
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The re-draft did not run. Nothing was written.",
      );
      setFocusRequest({ kind: "failure" });
    } finally {
      setPending(null);
    }
  }

  /**
   * The rehearsal control. It removes the TD's own unconfirmed adjustments as well as the
   * engine's drafts — that is what makes it useful and what makes the question it asks
   * matter, so the dialog says it in as many words before anything is removed.
   */
  async function runClearDrafts(kind: "day" | "stage"): Promise<void> {
    if (pending !== null) return;
    setClearPrompt(null);
    setPending({ kind: "clear" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    setClearResult(null);
    setStageResult(null);
    try {
      const cleared = await writer.clearDrafts(bulkScope, kind);
      setClearResult(cleared);
      // RUN 82I (QA pass 4) — one sentence in the notice, as every other press leaves, and the focus
      // on it. The strip below says it day by day, but it is drawn afresh by each press — which a
      // screen reader may not announce — and a focused strip had no name and no visible ring.
      setNote(clearNotice(cleared));
      setFocusRequest({ kind: "note" });
      if (cleared.view !== null) setView(cleared.view);
      await refreshSchedule();
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "Nothing was cleared. The appointments are unchanged.",
      );
      setFocusRequest({ kind: "failure" });
    } finally {
      setPending(null);
    }
  }

  /**
   * `Confirm Day` — the CC-Q25 sign-off of the day on screen.
   *
   * RUN 82D (QA pass 2) — the `try` holds the write and nothing else. The notice used to be worded
   * inside it, so when an API rolled back past RUN 82A answered without `skippedOfficial`, the
   * wording threw and the catch reported a sign-off that HAD happened as one that did not run.
   * The field is read as `?? []` now, and the reply is landed and worded only once the write's own
   * `try` has closed: whatever goes wrong in the wording, it can never be said as a failed write.
   */
  async function runConfirm(): Promise<void> {
    if (pending !== null) return;
    setPending({ kind: "confirm" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    let confirmed: ConfirmResponseBody;
    try {
      confirmed = await writer.confirm(scope);
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The sign-off did not run. Nothing was confirmed.",
      );
      return;
    } finally {
      setPending(null);
    }
    setView(confirmed);
    // RUN 82A — a day holding an Official match is signed off around it rather than refused
    // over it, so the notice names what was left alone as well as counting what was done.
    setNote(
      withSentence(
        `Confirmed · ${String(confirmed.confirmedSlots)} slot${
          confirmed.confirmedSlots === 1 ? "" : "s"
        } signed off across ${String(confirmed.confirmedFixtures.length)} fixture${
          confirmed.confirmedFixtures.length === 1 ? "" : "s"
        }.`,
        officialSkipNote(confirmed.skippedOfficial ?? []),
      ),
    );
  }

  /**
   * RUN 82 — `Mark Day Locked`, once the TD has said yes (§A11).
   *
   * The day sent is the day the page is appointing, which is the day `Confirm Day` signs off, and
   * the reply replaces the day view exactly as the sign-off's does: it IS that day, recomputed.
   * Nothing is guessed on this side about whether the day is ready — every refusal
   * (`appointments_not_ready_to_lock`, naming each match) is the server's, and is said in the
   * server's own words on the failure strip, with the day on screen left as it was.
   *
   * RUN 82A (QA pass 1) — one write at a time. `Lock The Day` is disabled while anything else is
   * out, and this returns before touching a thing if it is ever reached anyway: a second write
   * started here would share the one `pending`, and its `finally` would clear the busy state of
   * the write still running. The question stays open, so the answer can be given once that
   * write is done. A reply that locked nothing on a day that is not done is a failure, not a
   * notice (`lockNotice`), and the focus follows whichever of the two is drawn.
   *
   * RUN 82D (QA pass 2) — as `runConfirm`: the `try` holds the write alone, and the reply is landed
   * and worded after it, so a notice that cannot be worded is never reported as a lock that did not
   * run. The failure `lockNotice` can return is a reading of the reply, not a thrown error.
   */
  async function runLock(): Promise<void> {
    if (pending !== null) return;
    setLockPrompt(false);
    setPending({ kind: "lock" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    let locked: LockResponseBody;
    try {
      locked = await writer.lock(scope.day);
    } catch (error) {
      setFailure(
        error instanceof ApiError ? error.message : "The day was not locked. Nothing was changed.",
      );
      setFocusRequest({ kind: "failure" });
      return;
    } finally {
      setPending(null);
    }
    setView(locked);
    const outcome = lockNotice(locked, scope.day);
    if (outcome.kind === "note") {
      setNote(outcome.text);
      setFocusRequest({ kind: "note" });
    } else {
      setFailure(outcome.text);
      setFocusRequest({ kind: "failure" });
    }
  }

  /**
   * RUN 82 — `Unlock`, once the TD has said yes (§A11).
   *
   * **The reply is landed, not adopted.** The route answers with the reopened match's WHOLE day,
   * and since RUN 39 a match on any day can be on screen — so an unlock pressed on yesterday's row
   * answers with yesterday. Adopted as the day view, that reply would swap the Match Day, the day
   * counts and `Confirm Day` out from under the TD, which is precisely the seam `landSlotView`
   * closed for the per-slot writes. On the working day it replaces the day view, exactly as
   * `Confirm Day`'s reply does; on any other day it is folded into the backdrop.
   *
   * RUN 82A (QA pass 1) — three more things, each a finding:
   *
   *  * **One write at a time**, exactly as `runLock` — see there.
   *  * **An unlock on another day says which day.** `Confirm Day` and `Mark Day Locked` act on
   *    the day the Match Day dropdown names, so `Needs Re-Push until the day is locked again`
   *    sent the TD to two presses that would have signed off and locked the WRONG day. The notice
   *    names the match's own day and the dropdown he must move first.
   *  * **A refusal that means the match moved on is followed by a quiet re-read of its day.**
   *    `appointment_not_locked` and `appointments_frozen` both say another session (or TMS) got
   *    there first, and the row that offered `Unlock` is now wrong. The day is read again and
   *    landed as any reply is; the refusal stays on screen in the server's words, and a failed
   *    read changes nothing. It is awaited inside the write, so nothing else can start — and be
   *    overwritten by an older read — while it is out.
   */
  async function runUnlock(prompt: UnlockPrompt): Promise<void> {
    if (pending !== null) return;
    setUnlockPrompt(null);
    setPending({ kind: "unlock", tmsMatchDbId: prompt.tmsMatchDbId });
    setNote(null);
    setFailure(null);
    setPicker(null);
    try {
      const reopened = await writer.unlock(prompt.tmsMatchDbId);
      const elsewhere = otherDayOf(reopened);
      await landSlotView(reopened);
      setNote(
        elsewhere === null
          ? `${prompt.displayMatchNo} unlocked · TO, SJ and TJ can be corrected · Needs Re-Push ` +
              `until the day is locked again.`
          : `${prompt.displayMatchNo} unlocked · ${dayLabel(elsewhere)} — choose that day in ` +
              `Match Day, correct the crew, then Confirm Day and Mark Day Locked.`,
      );
      setFocusRequest({ kind: "note" });
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : `${prompt.displayMatchNo} was not unlocked. Nothing was changed.`,
      );
      setFocusRequest({ kind: "failure" });
      if (isMovedOnRefusal(error)) await rereadDay(prompt.date);
    } finally {
      setPending(null);
    }
  }

  /**
   * RUN 82A — re-read one day and land it where it belongs, saying nothing either way.
   *
   * It follows a refusal that is already on screen, so its own failure would only be a second
   * banner about the same press; the row simply stays as it was drawn.
   */
  async function rereadDay(day: string): Promise<void> {
    if (writer.readDay === undefined) return;
    try {
      await landSlotView(await writer.readDay({ day }));
    } catch {
      /* The row stays as it was drawn, beside the refusal that explains it. */
    }
  }

  /* -- RUN 84 - the TMS push through the Control Centre extension ------------------------------- */

  /**
   * The day this panel is appointing, for the life of the panel (the page keys it on the day). A push
   * runs for minutes and a recovery starts on mount, so their landings compare against this rather
   * than against state read when they began.
   */
  const panelDay = initialView.scope.day ?? scope.day;

  const timing: TmsPushTiming = { ...DEFAULT_TMS_PUSH_TIMING, ...pushTiming };
  const reportAttempts = Math.max(1, timing.reportAttempts);

  /** What one extension press needs from the page. Every piece of it is a prop or a setter. */
  function pushDeps(): TmsPushFlowDeps {
    return {
      bridge: extension,
      prepare: writer.preparePush,
      report: writer.reportPush,
      release: writer.releasePush,
      ...(writer.readPushReceipts === undefined ? {} : { receipts: writer.readPushReceipts }),
      memory: pushMemory,
      mintPressId: newPressId,
      clock: steadyClock,
      sleep: (ms) =>
        new Promise<void>((resolve) => {
          setTimeout(resolve, ms);
        }),
      timing,
      onStep: (step, press, attempt) => {
        setPushFlow((current) =>
          current === null
            ? current
            : {
                kind: "working",
                competitionId: press?.competitionId ?? current.competitionId,
                day: press?.day ?? (current.kind === "confirm" ? scope.day : current.day),
                step,
                press: press ?? (current.kind === "working" ? current.press : null),
                attempt: attempt ?? 1,
                attempts: reportAttempts,
              },
        );
      },
      gone: () => !mountedRef.current,
    };
  }

  /**
   * The day as the report answered it, landed where it belongs - the working day replaced, another
   * day folded into the backdrop - and the backdrop read again, so the locks the push wrote and their
   * chips show on every row and in every table (section A9a: "the day as it now stands").
   */
  async function landPushAnswer(answer: ExtensionPushReportBody): Promise<void> {
    const dayView: AppointmentsViewBody = {
      scope: answer.scope,
      fixtures: answer.fixtures,
      warnings: answer.warnings,
      workloads: answer.workloads,
      balanced: answer.balanced,
      counts: answer.counts,
    };
    if (dayView.scope.day === null || dayView.scope.day === panelDay) {
      setView(dayView);
    } else {
      setSchedule((current) => (current === null ? current : foldDayIntoSchedule(current, dayView)));
    }
    if (writer.readSchedule === undefined) return;
    try {
      const fresh = await writer.readSchedule();
      setSchedule((current) => (current === null ? current : fresh));
    } catch {
      /* The backdrop stays as it was. The day the push was about is already the server's. */
    }
  }

  /** A refusal that means the day moved on: read it again, quietly, and land it. */
  async function rereadPushDay(day: string): Promise<void> {
    if (writer.readDay === undefined) return;
    try {
      const fresh = await writer.readDay({ day });
      if (fresh.scope.day === null || fresh.scope.day === panelDay) {
        setView(fresh);
      } else {
        setSchedule((current) => (current === null ? current : foldDayIntoSchedule(current, fresh)));
      }
    } catch {
      /* The controls stay as they were drawn, beside the refusal that explains them. */
    }
  }

  /**
   * `Confirm Push` - one competition on the day the Match Day dropdown names, one TMS post
   * (`12-tms-push-extension.md` section 3). One write at a time: nothing starts while another write
   * is out, and the question stays modal - saying where the press is - until it has an answer.
   */
  async function runPush(competitionId: TmsCompetitionId): Promise<void> {
    if (pending !== null || unrecordedPress !== null) return;
    const day = scope.day;
    setPending({ kind: "push", competitionId });
    setNote(null);
    setFailure(null);
    setPicker(null);
    setPushStrips([]);
    setPushFlow({
      kind: "working",
      competitionId,
      day,
      step: "discovering",
      press: null,
      attempt: 1,
      attempts: reportAttempts,
    });
    const ending = await runTmsPush(pushDeps(), { day, competitionId });
    await finishPush(ending, competitionId, day);
  }

  /** `Report Again` - the SAME result, unchanged, after a report that may be sent again. */
  async function reportPushAgain(): Promise<void> {
    if (pending !== null) return;
    const flow = pushFlow;
    if (flow === null || flow.kind !== "answered" || flow.ending.kind !== "report-refused") return;
    const { press, result } = flow.ending;
    setPending({ kind: "push", competitionId: press.competitionId });
    setPushFlow({
      kind: "working",
      competitionId: press.competitionId,
      day: press.day,
      step: "reporting",
      press,
      attempt: 1,
      attempts: reportAttempts,
    });
    const ending = await reportTmsPush(pushDeps(), press, result);
    await finishPush(ending, press.competitionId, press.day);
  }

  /**
   * How a press ended, on the page. A press that prepared nothing closes its question and says so on
   * the failure strip, as every refused write on this page does; a press with an answer - reported,
   * refused at the report, or with no result to report - keeps the question open to say it, match by
   * match, until it is closed.
   */
  async function finishPush(
    ending: TmsPushEnding,
    competitionId: TmsCompetitionId,
    day: string,
  ): Promise<void> {
    if (ending.kind === "stopped") return;
    // What the tab still remembers now the press has ended: a result kept to be recorded, or nothing.
    // Fix round 1 (P10): a day push's memory counts too, and is never read past.
    setUnrecordedPress(rememberedAfterPush());
    // The press asked the extension itself: whatever it found is what the controls now say.
    setPresence(ending.kind === "absent" ? "absent" : "present");
    if (ending.kind === "absent" || ending.kind === "refused") {
      // Awaited while the write is still out, as `Unlock`'s re-read is: nothing else can start - and
      // be overwritten by this older read - meanwhile.
      if (ending.kind === "refused" && isMovedOnPushRefusal(ending.error)) await rereadPushDay(day);
      setPushFlow(null);
      setPending(null);
      setFailure(
        ending.kind === "absent"
          ? ABSENT_AT_THE_PRESS
          : prepareRefusal(ending.error, competitionId, day),
      );
      setFocusRequest({ kind: "failure" });
      return;
    }
    // The day as the report - or the release - answered it: the locks written, or the day freed.
    if (ending.kind === "reported" || ending.kind === "released") await landPushAnswer(ending.answer);
    // A release recorded before its answer failed (500, the day not read back): read the day again.
    if (ending.kind === "release-refused" && ending.error instanceof ApiError && ending.error.code === "push_completed_view_failed") {
      await rereadPushDay(day);
    }
    const answered: TmsPushAnsweredEnding = ending;
    setPending(null);
    setPushFlow({
      kind: "answered",
      competitionId: answered.press.competitionId,
      day: answered.press.day,
      ending: answered,
      // The hold is a duration counted on this device, never the laptop's clock against the server's.
      lapsed: holdLapsed(answered.press, steadyClock(), 0),
    });
  }

  /**
   * `Close` - the answer leaves the question for the page: its one sentence on the notice (or, where
   * nothing was accepted, the failure strip), with the focus; and, for a reported push, the strip
   * with every match and any collateral warning, until the next push.
   */
  function closePush(): void {
    const flow = pushFlow;
    if (flow === null || flow.kind !== "answered") return;
    setPushFlow(null);
    const ending = flow.ending;
    if (ending.kind === "reported") {
      const headline = answerHeadline(ending.answer, ending.result, ending.press);
      setPushStrips([{ press: ending.press, result: ending.result, answer: ending.answer }]);
      if (ending.answer.outcome === "failed" && !ending.result.cancelled) {
        setFailure(headline);
        setFocusRequest({ kind: "failure" });
      } else {
        setNote(headline);
        setFocusRequest({ kind: "note" });
      }
      return;
    }
    setFailure(
      ending.kind === "report-refused"
        ? reportRefusal(ending.error, ending.press, ending.retryable, false)
        : ending.kind === "released"
          ? releasedText(ending.notTaken, ending.press)
          : ending.kind === "release-refused"
            ? releaseRefusal(ending.error, ending.notTaken, ending.press)
            : noResultText(ending.why, ending.press, flow.lapsed, ending.recorded),
    );
    setFocusRequest({ kind: "failure" });
  }

  /* -- The day push (12-tms-push-extension.md section 6c; Decision #5 = B, 19 Sep 2026) ---------- */

  /**
   * What one day push needs from the page - the single press's own pieces (the bridge, the report,
   * the release, the receipts read, the clock, the timing), with the day's prepare and the day's
   * memory. Its steps and each press's ending land in the day push's question.
   */
  function dayPushDeps(): TmsDayPushFlowDeps {
    return {
      bridge: extension,
      prepareDay: writer.preparePushDay,
      report: writer.reportPush,
      release: writer.releasePush,
      ...(writer.readPushReceipts === undefined ? {} : { receipts: writer.readPushReceipts }),
      dayMemory: pushDayMemory,
      mintPressId: newPressId,
      clock: steadyClock,
      sleep: (ms) =>
        new Promise<void>((resolve) => {
          setTimeout(resolve, ms);
        }),
      timing,
      onStep: (step, press, attempt) => {
        setDayFlow((current) =>
          current === null || current.kind === "answered"
            ? current
            : {
                kind: "working",
                day: press?.day ?? (current.kind === "working" ? current.day : scope.day),
                step,
                press,
                hold:
                  press !== null && press.holdUntil !== null
                    ? press
                    : current.kind === "working"
                      ? current.hold
                      : null,
                attempt: attempt ?? 1,
                attempts: reportAttempts,
                ended: current.kind === "working" ? current.ended : [],
              },
        );
      },
      onPressEnded: (press, ending) => {
        setDayFlow((current) =>
          current === null || current.kind !== "working"
            ? current
            : {
                ...current,
                ended: [
                  ...current.ended.filter((entry) => entry.press.pressId !== press.pressId),
                  { press, ending },
                ],
              },
        );
      },
      gone: () => !mountedRef.current,
    };
  }

  /** What the tab still remembers after a push has ended - a single press's, or a day push's first. */
  function rememberedAfterPush(): RememberedPress | null {
    return pushMemory.read() ?? pushDayMemory.read()[0] ?? null;
  }

  /**
   * `Confirm Push` on the day push's question - every competition played on the day the Match Day
   * dropdown names, Men's first, in ONE press and one Confirm in the extension (section 6c). One write
   * at a time: nothing starts while another write is out or a press is still to be recorded, and the
   * question stays modal - saying where the day push is - until it has an answer.
   */
  async function runPushDay(): Promise<void> {
    if (pending !== null || unrecordedPress !== null) return;
    const day = scope.day;
    const competitionIds = competitionsOnDay(view.fixtures, day);
    setPending({ kind: "push-day" });
    setNote(null);
    setFailure(null);
    setPicker(null);
    setPushStrips([]);
    setDayFlow({
      kind: "working",
      day,
      step: "discovering",
      press: null,
      hold: null,
      attempt: 1,
      attempts: reportAttempts,
      ended: [],
    });
    const ending = await runTmsDayPush(dayPushDeps(), { day, competitionIds });
    await finishDayPush(ending, day);
  }

  /** `Report Again` on a day push's answer - each kept result not yet recorded, UNCHANGED, in order. */
  async function reportDayPushAgain(): Promise<void> {
    if (pending !== null) return;
    const flow = dayFlow;
    if (flow === null || flow.kind !== "answered") return;
    const first = flow.presses.find(({ ending }) => ending.kind === "report-refused" && ending.retryable);
    if (first === undefined) return;
    setPending({ kind: "push-day" });
    setDayFlow({
      kind: "working",
      day: flow.day,
      step: "reporting",
      press: first.press,
      hold: first.press,
      attempt: 1,
      attempts: reportAttempts,
      // Final round (M1): the live list starts empty, as the first run's does, so each verdict of
      // this run reaches its live region once - as one addition. Started with the presses' old
      // verdicts, a press reported again REPLACED its own old line: an addition and a change of
      // words at once, which some screen readers announce twice. The answer after it lists every
      // press again, each in its section.
      ended: [],
    });
    const ending = await reportTmsDayPushAgain(dayPushDeps(), {
      kind: "done",
      day: flow.day,
      presses: flow.presses,
      leftOut: flow.leftOut,
      answers: flow.answers,
      recovered: flow.recovered,
    });
    await finishDayPush(ending, flow.day);
  }

  /**
   * How a day push ended, on the page - as a single press's ending is: one that prepared nothing
   * closes its question and says so on the failure strip; one with an answer keeps the question open
   * to say it, competition by competition, until it is closed. The day is landed as the LAST answer
   * left it: each report answers with the whole day, and the last one follows every write before it.
   */
  async function finishDayPush(ending: TmsDayPushEnding, day: string): Promise<void> {
    if (ending.kind === "stopped") return;
    setUnrecordedPress(rememberedAfterPush());
    setPresence(ending.kind === "absent" ? "absent" : "present");
    if (ending.kind === "absent" || ending.kind === "refused") {
      if (ending.kind === "refused" && isMovedOnPushRefusal(ending.error)) await rereadPushDay(day);
      setDayFlow(null);
      setPending(null);
      setFailure(ending.kind === "absent" ? ABSENT_AT_THE_PRESS : dayPrepareRefusal(ending.error, day));
      setFocusRequest({ kind: "failure" });
      return;
    }
    // Fix round 1 (P1): the NEWEST answer is the day as it now stands - the last to ARRIVE, which
    // after `Report Again` is the press reported again, not the last in plan order (whose answer is
    // older, and would put back rows as they were before the press now recorded).
    const newest = ending.answers[ending.answers.length - 1];
    if (newest !== undefined) await landPushAnswer(newest);
    // A release recorded before its answer failed (500, the day not read back): read the day again.
    if (
      ending.presses.some(
        ({ ending: pressed }) =>
          pressed.kind === "release-refused" &&
          pressed.error instanceof ApiError &&
          pressed.error.code === "push_completed_view_failed",
      )
    ) {
      await rereadPushDay(day);
    }
    setPending(null);
    setDayFlow({
      kind: "answered",
      day: ending.day,
      presses: ending.presses,
      leftOut: ending.leftOut,
      // Each hold is a duration counted on this device, never the laptop's clock against the server's.
      lapsed: ending.presses
        .filter(({ press }) => holdLapsed(press, steadyClock(), 0))
        .map(({ press }) => press.pressId),
      answers: ending.answers,
      recovered: ending.recovered,
    });
  }

  /**
   * `Close` on the day push's answer - its account, competition by competition, on the notice (or,
   * where nothing of any competition was accepted, the failure strip), with the focus; and a strip per
   * reported competition, with every match and any collateral warning, until the next push.
   */
  function closeDayPush(): void {
    const flow = dayFlow;
    if (flow === null || flow.kind !== "answered") return;
    setDayFlow(null);
    setPushStrips(
      flow.presses.flatMap(({ ending }) =>
        ending.kind === "reported" ? [{ press: ending.press, result: ending.result, answer: ending.answer }] : [],
      ),
    );
    const text = dayAnswerNotice(flow.presses, flow.leftOut, flow.day, flow.lapsed, flow.recovered);
    if (dayAnswerIsFailure(flow.presses)) {
      setFailure(text);
      setFocusRequest({ kind: "failure" });
    } else {
      setNote(text);
      setFocusRequest({ kind: "note" });
    }
  }

  /**
   * The one write behind both routes: fill an empty slot, or reassign a filled one.
   *
   * §A7's first step is the same either way — sent deliberately without the flag, and
   * expected to be refused on a country conflict — so the branch is only over *how* the slot
   * is addressed, never over what the refusal means.
   */
  async function sendPick(
    target: SlotTarget,
    tmsPersonId: string,
    acceptCountryConflict?: true,
  ): Promise<AppointmentsViewBody> {
    const flag = acceptCountryConflict === undefined ? {} : { acceptCountryConflict: true };
    return target.kind === "edit"
      ? writer.patch(target.appointmentId, { tmsPersonId, ...flag })
      : writer.create({
          tmsMatchDbId: target.tmsMatchDbId,
          slot: target.slot,
          tmsPersonId,
          ...flag,
        });
  }

  /**
   * RUN 39 — land a per-slot write's reply on whichever day it actually describes.
   *
   * *"remove the restriction … allowing the TD to update these to play around with different
   * combination"* (Technical Delegate, 30 August 2026). Every row in the Fixture List is
   * editable now, on every day, which means a reply can arrive about a day the panel is not
   * appointing. Three outcomes, and the branch is over one question only:
   *
   *  * **It IS the working day** — or the page has no day at all — and the reply is adopted
   *    whole, exactly as it always was. The counts, the day warnings and `Confirm Day` are
   *    that day's and they should move.
   *  * **It is another day** and the reply is folded into the backdrop instead. The dropdown,
   *    the `N of 18 slots filled` line, the DAY WARNINGS counts and what `Confirm Day` would
   *    sign off are untouched, because none of them reads the backdrop.
   *  * Either way the schedule is re-read afterwards where it can be, so the three derived
   *    tables — which RUN 37 widened to the whole tournament — recompute against the server's
   *    own arithmetic rather than against the fold.
   *
   * The fold is not merely an optimisation on the way to that re-read: `readSchedule` is
   * optional (a component test without a network has none), and the edited row must show the
   * new name whether or not a second read is available.
   */
  async function landSlotView(next: AppointmentsViewBody): Promise<void> {
    if (otherDayOf(next) === null) {
      setView(next);
      return;
    }
    setSchedule((current) => (current === null ? current : foldDayIntoSchedule(current, next)));
    await refreshSchedule();
  }

  /**
   * The day a reply describes, where that is NOT the day being appointed — else `null`.
   *
   * The one question `landSlotView` branches on. RUN 82A lifted it out so the Unlock notice that
   * names another day and the landing that puts the reply on that day ask it in the same words
   * and can never disagree. A reply with no day, or a page with none, is the working day.
   */
  function otherDayOf(next: AppointmentsViewBody): string | null {
    const day = next.scope.day;
    if (day === null || appointingDay === null || day === appointingDay) return null;
    return day;
  }

  /** Step one of §A7 — sent deliberately without the flag, and refused on a conflict. */
  async function pickOfficial(
    fixture: AppointmentFixtureBody,
    slot: AppointmentSlotBody,
    tmsPersonId: string,
    option: PickerOption,
  ): Promise<void> {
    // RUN 82D — the one write no disabled press stands in front of: the name of the row that is
    // SAVING stays live (`busy && !saving`), so its popover can be opened and a second pick sent
    // while the first is out, sharing the one `pending` and banking a `previous` that is not the
    // server's. It is refused here, before anything moves.
    if (pending !== null) return;
    setPicker(null);
    // RUN 82G (QA pass 3) — the popover closes with the pick, and the option that held the focus
    // goes with it; the focus goes back to the slot's name, as the popover's own close gives it.
    setFocusRequest({ kind: "element", ids: [slotNameId(fixture.tmsMatchDbId, slot.slot)] });

    const official = officials.find((entry) => entry.tmsPersonId === tmsPersonId);
    if (official === undefined) return;

    const target: SlotTarget =
      slot.id === null
        ? { kind: "create", tmsMatchDbId: fixture.tmsMatchDbId, slot: slot.slot }
        : { kind: "edit", appointmentId: slot.id };
    const row = rowKey(fixture.tmsMatchDbId, slot.slot);

    /* RUN 39 — the optimistic cell goes on whichever copy of the match is on screen. A match
       off the working day is drawn from the backdrop and not from the day view, so putting the
       picked name on the day view would leave the row the TD just clicked unchanged until the
       server answered. Both are banked so either can be put back on a refusal. */
    const previous = view;
    const previousSchedule = schedule;
    const onWorkingDay = view.fixtures.some(
      (entry) => entry.tmsMatchDbId === fixture.tmsMatchDbId,
    );
    if (onWorkingDay) {
      setView(withOptimisticSlot(previous, fixture.tmsMatchDbId, slot.slot, official));
    } else if (previousSchedule !== null) {
      setSchedule(
        withOptimisticSlot(previousSchedule, fixture.tmsMatchDbId, slot.slot, official),
      );
    }
    setPending({ kind: "slot", row });
    setSlotError(null);
    setNote(null);
    // RUN 82M (QA pass 6) — and the failure strip, as every other write clears it.
    setFailure(null);

    try {
      await landSlotView(await sendPick(target, tmsPersonId));
      setNote(
        `${fixture.displayMatchNo} ${slot.slot} · ${slot.mediaName ?? "Unfilled"} → ` +
          `${official.mediaName} · matrix, tech table and teams coverage updated — review the ` +
          `impact before you sign the day off.` +
          (option.flag?.kind === "not_recommended" ? ` Advisory: ${option.flag.detail}` : ""),
      );
      // RUN 82K (QA pass 5) — and once it has landed, the focus is handed back again: a pick that
      // fixed what a Day Warnings filter was showing takes its row out of the list, and the focus
      // with it; the notice is where it then goes.
      setFocusRequest({ kind: "element", ids: [slotNameId(fixture.tmsMatchDbId, slot.slot)] });
    } catch (error) {
      // Whatever the answer was, the optimistic cell goes back first. The panel never keeps a
      // change the server did not take — and that is the whole of the reconciliation owed to
      // `appointment_slot_filled`, whose body carries a refusal and no day. The last view the
      // server sent is the one that goes back on screen, with the server's own sentence beside
      // the slot it happened to.
      if (onWorkingDay) setView(previous);
      else setSchedule(previousSchedule);
      setNote(null);
      if (isCountryConflictRefusal(error)) {
        // RUN 82G (QA pass 3) — a question opened by a refusal does not pass through
        // `openQuestion`, so it closes the popover itself. A popover left open under it could
        // send a pick the question knew nothing about.
        setPicker(null);
        setOverride({
          target,
          row,
          tmsMatchDbId: fixture.tmsMatchDbId,
          tmsPersonId,
          slot: slot.slot,
          officialName: official.mediaName,
          officialCountry: official.country,
          displayMatchNo: fixture.displayMatchNo,
          teams: teamsOf(fixture),
          message: (error as ApiError).message,
          date: fixture.date,
        });
      } else {
        const message =
          error instanceof ApiError
            ? error.message
            : "The appointment was not changed. The slot has been put back.";
        setSlotError({ row, message });
        // RUN 82D (QA pass 2) — `appointment_held` says the slot moved on under the edit, and the
        // row put back above is the stale one that offered it. Its day is re-read and landed, so
        // the row stops offering a press the server will refuse again; the refusal stays beside
        // the slot. Awaited inside the write, as `runUnlock`'s re-read is, so nothing else can
        // start — and be overwritten by this older read — while it is out.
        if (isHeldRefusal(error)) await rereadDay(fixture.date);
        // RUN 82I (QA pass 4) — and the focus goes back into the row: to the name, or where the
        // re-read has made the slot read-only, to the refusal on it.
        setFocusRequest({
          kind: "element",
          ids: [
            slotNameId(fixture.tmsMatchDbId, slot.slot),
            slotErrorId(fixture.tmsMatchDbId, slot.slot),
          ],
          refusal: message,
        });
      }
    } finally {
      setPending(null);
    }
  }

  /** Step two of §A7 — the explicit, recorded override, on whichever route step one took. */
  async function acceptOverride(prompt: OverridePrompt): Promise<void> {
    if (pending !== null) return;
    setOverride(null);
    setPending({ kind: "slot", row: prompt.row });
    setSlotError(null);
    // RUN 82K (QA pass 5) — as every other write: what an earlier press said is not left standing,
    // where the focus hand-off could fall back to it as if it were this answer's.
    setNote(null);
    setFailure(null);
    let refusal: string | null = null;
    try {
      // RUN 39 — the override lands the same way the first step would have, on whichever day
      // the reply is about. §A7's two steps are unchanged; only where the answer is put is.
      await landSlotView(await sendPick(prompt.target, prompt.tmsPersonId, true));
      setNote(
        `Overridden · ${prompt.displayMatchNo} — country conflict accepted; the slot stays flagged.`,
      );
    } catch (error) {
      refusal =
        error instanceof ApiError
          ? error.message
          : "The override was not recorded. The slot is unchanged.";
      setSlotError({ row: prompt.row, message: refusal });
      // RUN 82D — the slot can be signed off or locked between the two steps as well.
      if (isHeldRefusal(error)) await rereadDay(prompt.date);
    } finally {
      setPending(null);
    }
    // RUN 82G (QA pass 3) — either way, back to the slot the answer was about. What happened is
    // announced where it is drawn: the notice's live region, or the refusal's alert on the slot.
    setFocusRequest({
      kind: "element",
      ids: [
        slotNameId(prompt.tmsMatchDbId, prompt.slot),
        slotErrorId(prompt.tmsMatchDbId, prompt.slot),
      ],
      ...(refusal === null ? {} : { refusal }),
    });
  }

  async function runWithdraw(prompt: WithdrawPrompt): Promise<void> {
    if (pending !== null) return;
    setWithdraw(null);
    setPending({ kind: "slot", row: prompt.row });
    setSlotError(null);
    setNote(null);
    setFailure(null);
    let refusal: string | null = null;
    try {
      await landSlotView(await writer.withdraw(prompt.appointmentId));
      setNote(`Withdrawn · ${prompt.displayMatchNo} ${prompt.slot} is unfilled again.`);
    } catch (error) {
      // `appointment_held` arrives here when the row was signed off, locked, pushed or reopened
      // between the screen drawing the button and the TD pressing it. Nothing optimistic was
      // applied to a withdrawal, so the refusal is said on the slot it belongs to — and, RUN 82D,
      // the slot's day is then re-read quietly, because the row that offered `Withdraw` is the
      // one the server has moved past, and pressing it again would only be refused again.
      refusal =
        error instanceof ApiError
          ? error.message
          : "The appointment was not withdrawn. The slot is unchanged.";
      setSlotError({ row: prompt.row, message: refusal });
      if (isHeldRefusal(error)) await rereadDay(prompt.date);
    } finally {
      setPending(null);
    }
    // RUN 82G (QA pass 3) — as the override: back into the row, on the slot's name. `Withdraw`
    // itself is gone once the slot is empty, and on a refusal the slot may no longer offer it.
    setFocusRequest({
      kind: "element",
      ids: [
        slotNameId(prompt.tmsMatchDbId, prompt.slot),
        slotErrorId(prompt.tmsMatchDbId, prompt.slot),
      ],
      ...(refusal === null ? {} : { refusal }),
    });
  }

  /**
   * §A10's Export — the day as it is displayed, as a two-sheet `.xlsx`.
   *
   * It reads state and writes nothing, which is why it is not a `Pending` and does not make the
   * panel busy: a copy of the day can be taken while a draft is running, and what comes out is
   * whatever the fixture list and the matrix are showing at the moment of the press. Both
   * sheets carry their OWN panel's filters in their scope line, because Part C invariant 2 lets
   * the two tabs sit differently and a single scope sentence would be wrong about one of them.
   */
  async function runExport(): Promise<void> {
    setExporting(true);
    setFailure(null);
    try {
      // One instant for both the sheet and the filename. Two calls could straddle a minute
      // boundary and stamp the file a minute away from the sheet inside it.
      const at = new Date();
      const day = view.scope.day ?? scope.day ?? null;
      const stageWord =
        fixStage === "all" ? null : fixStage === "pool" ? "Pool matches" : "Post-pool matches";
      // Rebuilt unfiltered for the sheet, not read from `matrixTable` — see the matrixScope
      // note below for why the exported matrix ignores the panel's toggles.
      const exportMatrix = buildMatrix(listSource, matrixOfficials(listView, officials, "all"));
      const workbook = buildTechnicalWorkbook({
        tournament: tournament?.shortDisplayName ?? null,
        timezone: tournament?.timezone ?? null,
        at,
        fixtureScope: scopeLine(
          [
            // RUN 37 — the sheet is *"as displayed"*, and what is displayed is now the whole
            // schedule. So the scope line names the days the rows actually cover rather than
            // the day the page is appointing; a sheet headed `Match day 18 Sep` with a
            // fortnight of matches under it would be the export lying about itself. Each row
            // already carries its own date in the `Date` column, so nothing per-row changes.
            exportedDays(listedFixtures, day),
            fixComp === "all" ? null : competitionLabel(fixComp),
            stageWord,
          ],
          listedFixtures.length,
        ),
        matrixScope: scopeLine(
          [
            // The matrix SHEET is always the complete matrix — whole schedule, both
            // competitions, every official — whatever the panel's own toggles show. The
            // toggles are a viewing convenience; the workbook is the document that gets
            // SHARED, and a half-matrix that looks whole misleads whoever receives it. The
            // Technical Delegate proved the point on the first real export (31 Aug 2026): a
            // Women's-filtered matrix went out and read as everyone's totals, scope line
            // notwithstanding. The fixture sheet stays as-displayed — choosing which MATCHES
            // to share is a real use; an aggregate is only honest whole.
            exportedDays(listSource, day),
            "both competitions",
            "all officials",
          ],
          exportMatrix.fixtures,
        ),
        fixtures: technicalFixtureRows(listedFixtures),
        matrix: technicalMatrixRows(exportMatrix.rows),
      });
      const filename = exportFilename(workbook.base, at, tournament?.timezone ?? null);
      await exporter(workbook, filename);
      setNote(exportNotice(filename));
    } catch {
      setFailure("The workbook could not be written. Nothing has been downloaded.");
    } finally {
      setExporting(false);
    }
  }

  /** §A7's quick `Check` — pass or fail, said out loud, with the offending slot pulsing. */
  function runCheck(fixture: AppointmentFixtureBody): void {
    setFailure(null);
    const bad = fixture.slots.find((slot) => hasRuleBreach(slot));
    if (bad === undefined) {
      setPulsing(null);
      setNote(
        `Passed · ${fixture.displayMatchNo} — no country conflict, and nobody holds two roles.`,
      );
      return;
    }
    setPulsing(rowKey(fixture.tmsMatchDbId, bad.slot));
    setNote(
      `Conflict · ${fixture.displayMatchNo} — ${bad.mediaName ?? "the appointed official"} ` +
        `(${bad.country ?? "—"}) is appointed as ${bad.slot} to a match involving their own ` +
        `country.`,
    );
  }

  // `Draft The Pool Stage` → `the pool stage`; the wider fallback → `all remaining days`.
  // Taken from the button's own label so the dialog cannot name a scope the button does not.
  const stageScopeWord = stageLabel === "Draft The Pool Stage" ? "the pool stage" : "all remaining days";

  /**
   * The competitions the two bulk controls may be aimed at.
   *
   * Falls back to the day on screen when the page did not supply the schedule — which is
   * every test that renders this component on its own, and is honest there: the choice then
   * offers what this screen can actually see rather than a pair it has invented.
   */
  const bulkChoices: readonly TmsCompetitionId[] =
    competitions ??
    [...new Set(view.fixtures.map((fixture) => fixture.competitionId))].sort();
  const bulkWord = bulkCompetitionWord(bulkComp);
  /** The scope the two bulk controls send. The day is the same; the competition is the choice. */
  const bulkScope: AppointmentScope =
    bulkComp === "all" ? scope : { ...scope, competition: bulkComp };

  /**
   * RUN 37 — which day the list is APPOINTING, and which day it OPENS at. Two different
   * questions with two different answers, and conflating them is the bug this build avoids:
   * the working day is chosen by the TD from the Match Day dropdown and drives every write,
   * while the landing day is only where the scroll bar starts.
   */
  const workingDay = schedule === null ? null : appointingDay;
  /**
   * What the three derived tables are counted over, in words and for the export's scope line.
   *
   * RUN 37 widened them from the day to the schedule after the TD ran `Draft The Pool Stage`,
   * watched ten days get written, and then read a matrix footnote telling him six matches had
   * been reflected. Where the schedule has not been read the wording is the old one, because
   * there the tables really are one day's.
   */
  const tableSpan = schedule === null ? "on this day" : "across the tournament";
  /**
   * The day the Fixture List's own pane opens at.
   *
   * **Counted over the rows the list is actually DRAWING, not over the whole schedule.** It
   * used to be `listSource` — every fixture, before the competition and stage tabs narrow it —
   * which is wrong in both directions once a tab is pressed: the anchor can name a day the
   * filtered table no longer contains (a rest day for the Men's, say), leaving the pane at the
   * top of the fortnight; and the rows above whichever day IS drawn have changed in number, so
   * the pane must be landed again or today slides up it. `fixturesInScope` is the comp/stage
   * narrowing and deliberately NOT the warning focus — that one is a temporary look at a
   * handful of rows and must not move the TD's scroll.
   *
   * `anchorDay` rather than `landingDay`: a list has no first-day fallback to make, so once
   * every match day is behind him it opens on the last day played rather than on Day 1.
   */
  const landOn = anchorDay(
    fixturesInScope.map((fixture) => fixture.date),
    tournamentToday(tournament?.timezone ?? null, now),
  );

  const drafting = pending?.kind === "draft";
  const draftingStage = pending?.kind === "stage";
  const redrafting = pending?.kind === "redraft";
  const clearing = pending?.kind === "clear";
  const confirming = pending?.kind === "confirm";
  const locking = pending?.kind === "lock";
  const busy = pending !== null;
  /**
   * RUN 84 - the day's push controls: one per competition played on the day the Match Day dropdown
   * names (the day `Confirm Day` and `Mark Day Locked` act on), each saying why it cannot be pressed
   * when it cannot - a write out, the extension absent, or nothing owed.
   */
  const pushViews = pushActionViews(view.fixtures, scope.day, presence, {
    pushing: pending?.kind === "push" ? pending.competitionId : null,
    busy,
    unrecorded: unrecordedPress,
  });
  /**
   * The day push (section 6c) - `Push Day To TMS`, for the same day, beside the per-competition
   * controls: it shares their reason while they share one, and otherwise can be pressed while any
   * competition of the day has a match to send.
   */
  const pushDayView = pushDayActionView(view.fixtures, scope.day, {
    pushingDay: pending?.kind === "push-day",
    groupReason: pushViews.groupReason,
  });
  /**
   * RUN 82 — whether `Mark Day Locked` has anything left to do (§A11).
   *
   * Read off the DAY view, because the press covers the day the Match Day dropdown names and the
   * list around it holds every other day too. A day with no matches is not "locked": `every` of
   * nothing is true, and a button that said `Day Locked` over an empty day would be the screen
   * claiming a record nobody made. The press stays live there and the server says why not.
   *
   * RUN 82A (QA pass 1) — "every match locked" was the wrong question once the route began to
   * skip an Official match nobody confirmed: a day holding one could never become `Day Locked`,
   * and every further press would lock nothing. The rule is `nothingLeftToLock`, shared with the
   * notice.
   */
  const dayLocked = nothingLeftToLock(view.fixtures);
  /**
   * RUN 82D (QA pass 2) — the matches on the working day that `Mark Day Locked` will leave exactly
   * as they are, so its question can count what the press covers and name what it does not. The
   * same test the button and the notice read (`skippedByTheLock`).
   */
  const lockSkips = view.fixtures.filter(skippedByTheLock);
  /**
   * RUN 82A — a question is already on screen.
   *
   * Every question on this page is drawn in the one `.overridebox` frame, over a scrim that
   * darkens the page but takes no clicks — deliberately, so a stray click cannot dismiss a
   * decision. What that leaves is that the presses behind the scrim still work, and the §A11 pair
   * could not afford it: `Unlock` on a second row silently re-aimed an open `Unlock M01` at M02,
   * and `Mark Day Locked` opened a second question over whichever was open. So while ANY question
   * is open, the two presses that open these do nothing.
   *
   * RUN 82D (QA pass 2) — nor does any other. The probe that found it opened `Clear Drafts` over an
   * open lock question and answered both. Three layers now, each enough on its own in a browser:
   * the workbench behind a question is `inert`, so nothing on it can be pressed, tabbed to or read
   * out; every press that opens a question or the officials popover returns while one is open;
   * and every answer is disabled while a write is out. `openQuestion` is the one way a question is
   * opened, so no future question can forget the rule.
   */
  const questionOpen =
    override !== null ||
    withdraw !== null ||
    stagePrompt ||
    redraftPrompt ||
    clearPrompt !== null ||
    lockPrompt ||
    unlockPrompt !== null ||
    pushFlow !== null ||
    dayFlow !== null;

  /**
   * RUN 82D — open a question, unless one is already open.
   *
   * It closes the officials popover on the way. The popover is drawn outside the workbench, so
   * `inert` does not reach it, and a TD who tabbed from an open popover back to `Withdraw` and
   * pressed Enter — no pointer, so its outside-click close never fired — was left with a live
   * popover under the question, able to start a write that the question knew nothing about.
   */
  function openQuestion(open: () => void): void {
    if (questionOpen) return;
    setPicker(null);
    open();
  }

  /**
   * RUN 82E — keyboard focus goes INTO whichever question opens. The workbench goes `inert` the
   * moment one does (RUN 82D), which takes focus off the press that opened it; the older questions
   * had no focus handling of their own, so it fell to the page body and a keyboard user had to
   * find the dialogue. Every question now receives it on its safe answer — `Cancel` — as the two
   * lock questions already did; focus the TD has already put inside the dialogue is left alone.
   *
   * RUN 82G (QA pass 3) — the question is looked for among THIS PANEL'S questions only
   * (`questionsRef`). The masthead draws its own alertdialog when Sign Out is pressed, before the
   * page in document order, and a search of the whole document handed it the focus.
   */
  useEffect(() => {
    if (!questionOpen) return;
    const dialog = questionsRef.current?.querySelector<HTMLElement>('[role="alertdialog"]') ?? null;
    if (dialog === null) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body && dialog.contains(active)) return;
    const buttons = Array.from(dialog.querySelectorAll<HTMLButtonElement>("button"));
    const cancel = buttons.find((button) => button.textContent?.trim() === "Cancel");
    // RUN 84 - a question with no button yet (the push, recovering as the page opens) takes the
    // focus itself.
    (cancel ?? buttons[0] ?? (dialog.hasAttribute("tabindex") ? dialog : undefined))?.focus();
  }, [questionOpen]);

  /**
   * RUN 84 - the push question changes under the focus as the press moves on: `Confirm Push` goes
   * as the press starts, and the answer brings `Close`. While the press runs the question itself
   * holds the focus (it has nothing to press - the only confirm and cancel are the extension's own);
   * once it has an answer, the answer's own sentence takes it (frontend review M3), so it is read
   * out and scrolled into view on a phone; Tab goes on from there to `Close` (`keepTabInside`).
   */
  const pushPhase = pushFlow === null ? null : pushFlow.kind;
  useEffect(() => {
    if (pushPhase === null || pushPhase === "confirm") return;
    const dialog =
      questionsRef.current?.querySelector<HTMLElement>('[data-testid="push-prompt"]') ?? null;
    if (dialog === null) return;
    if (pushPhase === "answered") {
      document.getElementById(PUSH_OUTCOME_ID)?.focus();
      return;
    }
    const active = document.activeElement;
    if (active !== null && active !== document.body && dialog.contains(active)) return;
    dialog.focus();
  }, [pushPhase]);

  /**
   * The day push's question (section 6c) moves under the focus as the single press's does: the frame
   * holds it while the day push runs, and the answer's own paragraph takes it when the answer arrives.
   */
  const dayPhase = dayFlow === null ? null : dayFlow.kind;
  useEffect(() => {
    if (dayPhase === null || dayPhase === "confirm") return;
    const dialog =
      questionsRef.current?.querySelector<HTMLElement>('[data-testid="push-day-prompt"]') ?? null;
    if (dialog === null) return;
    if (dayPhase === "answered") {
      document.getElementById(PUSH_DAY_OUTCOME_ID)?.focus();
      return;
    }
    const active = document.activeElement;
    if (active !== null && active !== document.body && dialog.contains(active)) return;
    dialog.focus();
  }, [dayPhase]);

  /**
   * RUN 82I (QA pass 4) — Tab cannot leave an open question from OUTSIDE it either.
   *
   * `keepTabInside` sits on each question and so acts only while the focus is already inside. A
   * click on a question's own words puts the focus on the page body, and a Tab from there walked
   * out to Match Day, How To Use and the module nav with the question still open. While one is
   * open, a Tab pressed anywhere else on the page brings the focus into it — forwards to its first
   * stop, backwards to its last — as the How To drawer's own trap does. Caught at the document
   * before anything else hears it; inside the question it is left to `keepTabInside` - including
   * the push question, whose own frame keeps the focus after a click on its words (RUN 84, H1).
   */
  useEffect(() => {
    if (!questionOpen) return;
    function bringTabInside(event: KeyboardEvent): void {
      if (event.key !== "Tab") return;
      const question = questionsRef.current?.querySelector<HTMLElement>('[role="alertdialog"]');
      if (question === null || question === undefined) return;
      const active = document.activeElement;
      if (active !== null && question.contains(active)) return;
      const stops = questionTabStops(question);
      const target = event.shiftKey ? stops[stops.length - 1] : stops[0];
      if (target === undefined) {
        // RUN 84 - a question with no stop of its own takes the focus itself.
        if (!question.hasAttribute("tabindex")) return;
        event.preventDefault();
        question.focus();
        return;
      }
      event.preventDefault();
      target.focus();
    }
    document.addEventListener("keydown", bringTabInside, true);
    return () => {
      document.removeEventListener("keydown", bringTabInside, true);
    };
  }, [questionOpen]);

  /**
   * Clicking a day-warning count narrows the Fixture List to the matches that carry it and
   * brings the list back into view. Clicking the same count again clears the narrowing —
   * a filter you cannot switch off is a trap, and the chip beside the list says it is on.
   */
  function focusWarnings(bucket: Exclude<WarningFocus, null>): void {
    setWarningFocus((current) => (current === bucket ? null : bucket));
    // On a wide screen the workbench is already in view and stays there (F-20), so this is a
    // no-op there and only matters on the stacked fallback. Guarded because `scrollIntoView`
    // is not implemented everywhere the panel is rendered, and a narrowing that throws on the
    // way to being applied would lose the filter the TD just asked for.
    const workbench = workbenchRef.current;
    if (typeof workbench?.scrollIntoView === "function") {
      workbench.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <>
      <div className="apptband">
        <h2>
          TO Appointments <span className="md">· {view.scope.day ?? scope.day}</span>
        </h2>
        <p className="num" data-testid="day-counts">
          <b>{view.counts.filled}</b> of <b>{view.counts.slots}</b> slots filled ·{" "}
          <b>{view.counts.confirmed}</b> confirmed · <b>{view.counts.locked}</b> locked ·{" "}
          <b>{view.counts.frozen}</b> fixture
          {view.counts.frozen === 1 ? "" : "s"} frozen
        </p>
      </div>

      {/* RUN 82A — the live region is always mounted and only the notice inside it comes and
          goes. The notice used to carry `role="status"` itself, so every write removed the
          region and a moment later inserted a new one with its words already in it — which a
          screen reader is entitled to miss, and often did. Text added to a region that was
          already there is what `status` is for. The notice takes focus after a §A11 answer, so
          it can hold it without being a stop in the tab order. */}
      <div role="status" data-testid="action-note-region">
        {note === null ? null : (
          <p className="apptnote" data-testid="action-note" tabIndex={-1} ref={noteRef}>
            {note}
          </p>
        )}
      </div>
      {failure === null ? null : (
        <div
          className="writeerror"
          role="alert"
          data-testid="action-failure"
          tabIndex={-1}
          ref={failureRef}
        >
          <b>Not Done</b>
          {failure}
        </div>
      )}

      {stageResult === null ? null : <StageResultStrip stage={stageResult} />}
      {clearResult === null ? null : <ClearResultStrip clear={clearResult} />}
      {pushStrips.map((strip) => (
        <TmsPushResultStrip key={strip.press.pressId} strip={strip} />
      ))}

      {dayWide.length === 0 ? null : (
        /* Day-wide warnings name no fixture, so there is no row to collapse them onto. They
           use the same line component as a fixture's, so the two cannot drift into two
           different renderings of one warning. */
        <div className="warnlist" data-testid="day-warnings">
          {dayWide.map((warning, index) => (
            <WarningLine
              key={`${warning.code}-${String(index)}`}
              warning={warning}
              officials={officials}
            />
          ))}
        </div>
      )}

      {/* RUN 82D (QA pass 2) — inert while any question is open, so the page behind a question
          can be neither pressed, tabbed to, nor read out: the modality the scrim only drew. Every
          question — and the notice region — is drawn OUTSIDE this element, as siblings below it
          and above it, so none of them is inside what it makes inert. */}
      <div className="workbench workbench--split" ref={workbenchRef} inert={questionOpen}>
        <div className="workbench__list">
          <FixtureListPanel
            fixtures={listedFixtures}
            allFixtures={listSource}
            landOn={landOn}
            scheduleArrived={schedule !== null}
            workingDay={workingDay}
            warnings={listWarnings}
            officials={officials}
            competition={fixComp}
            onCompetition={setFixComp}
            stage={fixStage}
            onStage={setFixStage}
            busy={busy}
            drafting={drafting}
            draftingStage={draftingStage}
            clearing={clearing}
            confirming={confirming}
            stageLabel={stageLabel}
            onDraft={() => void runDraft()}
            onDraftStage={() => {
              openQuestion(() => {
                setStagePrompt(true);
              });
            }}
            redrafting={redrafting}
            onReDraft={() => {
              openQuestion(() => {
                setRedraftPrompt(true);
              });
            }}
            onClearDrafts={() => {
              openQuestion(() => {
                setClearPrompt("day");
              });
            }}
            onConfirm={() => void runConfirm()}
            locking={locking}
            dayLocked={dayLocked}
            nothingToLock={dayLocked && !view.fixtures.some((fixture) => fixture.locked)}
            onLock={() => {
              openQuestion(() => {
                setLockPrompt(true);
              });
            }}
            onUnlock={(fixture) => {
              openQuestion(() => {
                setUnlockPrompt({
                  tmsMatchDbId: fixture.tmsMatchDbId,
                  displayMatchNo: fixture.displayMatchNo,
                  date: fixture.date,
                });
              });
            }}
            unlocking={pending?.kind === "unlock" ? pending.tmsMatchDbId : null}
            pushActions={
              <TmsPushActions
                actions={pushViews.actions}
                groupReason={pushViews.groupReason}
                groupNote={pushViews.groupNote}
                onPush={(competitionId) => {
                  if (pending !== null) return;
                  openQuestion(() => {
                    setPushFlow({ kind: "confirm", competitionId });
                  });
                }}
                dayAction={pushDayView}
                onPushDay={() => {
                  if (pending !== null) return;
                  openQuestion(() => {
                    setDayFlow({ kind: "confirm" });
                  });
                }}
              />
            }
            onExport={() => void runExport()}
            exporting={exporting}
            onCheck={runCheck}
            daySummary={warnSummaryStrip()}
            openSlot={
              picker === null
                ? null
                : { tmsMatchDbId: picker.fixture.tmsMatchDbId, slot: picker.slot.slot }
            }
            onOpenPicker={(fixture, slot, anchor) => {
              // RUN 82D — the popover is a way into a write, so it does not open under a question.
              // RUN 82G (QA pass 3) — nor while a write is out. The saving row's name stays live
              // (so the focus is not thrown off it), and its popover reopened there was still open
              // when that write's refusal raised the override question — live under it, and able to
              // send a second pick that the question then reported as its own.
              if (questionOpen || pending !== null) return;
              setPulsing(null);
              setPicker((current) =>
                current !== null &&
                current.fixture.tmsMatchDbId === fixture.tmsMatchDbId &&
                current.slot.slot === slot.slot
                  ? null
                  : { fixture, slot, anchor },
              );
            }}
            onWithdraw={(fixture, slot) => {
              const appointmentId = slot.id;
              if (appointmentId === null) return;
              openQuestion(() => {
                setWithdraw({
                  appointmentId,
                  row: rowKey(fixture.tmsMatchDbId, slot.slot),
                  tmsMatchDbId: fixture.tmsMatchDbId,
                  slot: slot.slot,
                  officialName: slot.mediaName ?? "the appointed official",
                  displayMatchNo: fixture.displayMatchNo,
                  date: fixture.date,
                });
              });
            }}
            pulsing={pulsing}
            savingRow={pending?.kind === "slot" ? pending.row : null}
            slotError={slotError}
            highlighted={null}
            onWarningFilter={setWarningFocus}
          />
        </div>

        {/* F-21 — the layout the Technical Delegate asked for on 20 August 2026, in his own
            words: *"keep the Fixture List on the top, move the Tech Table Appointments to the
            RIGHT of the Fixture List, and move the Appointment Matrix Table DOWN under the
            Fixture List"*.

            F-20 had put all three derived tables in one right-hand column, which made the
            fixture list a 45% pane the TD had to scroll sideways to read a match in. The list
            now takes the top-left and is wide enough to show all eight of its columns at once;
            the Tech Table — the narrowest of the three — rides beside it; and the two wide
            tables sit below, reached by page scroll. He proposed that trade-off himself.

            The list keeps its self-scrolling pane and sticky head so the Tech Table beside it
            stays in view while he works down the day, and all three tables keep the change
            flash. Below the split's breakpoint the four blocks stack in the same order. */}
        <div className="workbench__tech">
          <TechTablePanel
            rows={techRows}
            competition={techComp}
            onCompetition={setTechComp}
            role={techRole}
            onRole={setTechRole}
            balanced={view.balanced}
            flashing={flashing}
          />
        </div>

        <div className="workbench__matrix">
          <MatrixPanel
            span={tableSpan}
            table={matrixTable}
            competition={matComp}
            onCompetition={setMatComp}
            role={matRole}
            onRole={setMatRole}
            flashing={flashing}
          />
        </div>

        <div className="workbench__teams">
          <TeamsCoveragePanel
            span={tableSpan}
            table={teamsTable}
            competition={tfComp}
            onCompetition={setTfComp}
            role={tfRole}
            onRole={setTfRole}
            flashing={flashing}
          />
        </div>
      </div>

      {picker === null ? null : (
        <OfficialPicker
          fixture={picker.fixture}
          slot={picker.slot.slot}
          officials={officials}
          dayFixtures={pickerDayFixtures}
          anchor={picker.anchor}
          breached={hasRuleBreach(picker.slot)}
          onPick={(tmsPersonId, option) => {
            void pickOfficial(picker.fixture, picker.slot, tmsPersonId, option);
          }}
          onClose={() => {
            setPicker(null);
            picker.anchor.focus();
          }}
        />
      )}

      {/* RUN 82G (QA pass 3) — every question this panel draws, in one place the focus can be looked
          for without finding anybody else's dialogue. A plain block: each question inside it is
          fixed to the viewport, so it lays nothing out. It is outside the workbench, so `inert`
          never reaches it.

          And every question below is now modal the way the §A11 pair is: `aria-modal`, its body as
          its description, and Tab kept inside it (`keepTabInside`). */}
      <div ref={questionsRef} data-testid="appointment-questions">
        {/* RUN 82I (QA pass 4) — the scrim the frame draws is a shadow, and a shadow takes no clicks:
            the masthead, How To Use and the module nav behind it all still answered a click while a
            question was open. This transparent layer, just under the question, takes those clicks
            and does nothing with them — no dismiss, as the frame has never had one. */}
        {questionOpen ? (
          <div className="questionscrim" aria-hidden="true" data-testid="question-scrim" />
        ) : null}
        {override === null ? null : (
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="ovTitle"
            aria-describedby="ovWho ovApi ovRecord"
            data-testid="override-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="ovTitle">Hard-Line Warning · Confirm Override</b>
            <p id="ovWho">
              <b>
                {override.officialName} ({override.officialCountry})
              </b>{" "}
              would be appointed as <b>{override.slot}</b> to <b>{override.displayMatchNo}</b> —{" "}
              {override.teams}, a match involving their own country.
            </p>
            <p id="ovApi" className="overridebox__api">
              {override.message}
            </p>
            <p id="ovRecord">
              Overriding records your decision against your name, keeps the slot flagged, and the
              conflict still appears on the appointment report. Confirm override?
            </p>
            {/* RUN 82D (QA pass 2) — this answer, and the answer of each of the page's older questions
                below, is disabled while any write is out, as the lock layer's two have been since
                RUN 82A: an answer given then would start a second write beside the first. */}
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void acceptOverride(override)}
              >
                Confirm Override
              </button>
              <button
                type="button"
                className="gbtn"
                onClick={() => {
                  setOverride(null);
                  setFocusRequest({
                    kind: "element",
                    ids: [slotNameId(override.tmsMatchDbId, override.slot)],
                  });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!stagePrompt ? null : (
          /* The press crosses days, so it says exactly what it will do before it does it —
             including the three things it will not touch. §A8's own modal is the ancestor of
             this box; the copy is the TD's, agreed on 20 August 2026. */
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="stageTitle"
            aria-describedby={describedBy([
              "stageCopy",
              bulkChoices.length < 2 ? null : "stageCompetition",
              "stageEven",
              bulkComp === "all" || bulkChoices.length < 2 ? null : "stagePanel",
            ])}
            data-testid="stage-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="stageTitle">{stageLabel}</b>
            {bulkChoices.length < 2 ? null : (
              <fieldset className="clearscope" data-testid="stage-competition">
                <legend className="kicker">Which competition</legend>
                {(["all", ...bulkChoices] as BulkCompetition[]).map((choice) => (
                  <label key={choice}>
                    <input
                      type="radio"
                      name="stage-competition"
                      value={choice}
                      checked={bulkComp === choice}
                      onChange={() => {
                        setBulkComp(choice);
                      }}
                    />
                    {bulkCompetitionWord(choice)}
                  </label>
                ))}
              </fieldset>
            )}
            <p id="stageCopy" data-testid="stage-prompt-copy">
              Drafts every remaining pool-stage day in date order, one engine pass per day. Days
              already confirmed, locked, or hand-edited slots are left alone.
            </p>
            {bulkChoices.length < 2 ? null : (
              <p id="stageCompetition" data-testid="stage-prompt-competition">
                This press drafts <b>{bulkWord}</b>.
              </p>
            )}
            <p id="stageEven">
              Each day is drafted against the days before it, so the workload, the team coverage
              and the colleague pairings even out across the whole stage rather than day by day.
            </p>
            {bulkComp === "all" || bulkChoices.length < 2 ? null : (
              <p id="stagePanel" data-testid="stage-prompt-panel">
                The officials panel is shared, so the other competition&rsquo;s appointments on
                the same days are still counted &mdash; nobody is drafted as though their other
                matches did not happen. Nothing outside {bulkWord} is written.
              </p>
            )}
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runStageDraft()}
              >
                Run The Stage Draft
              </button>
              <button
                type="button"
                className="gbtn"
                onClick={() => {
                  setStagePrompt(false);
                  setFocusRequest({ kind: "element", ids: [DRAFT_STAGE_ID] });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!redraftPrompt ? null : (
          /* The stage-draft modal's idiom, with the copy the shuffle needs: what it produces
             (a different but equally even draft), and what it keeps (everything the TD has
             decided). It crosses days exactly as its sibling does, so it asks exactly as its
             sibling asks. */
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="redraftTitle"
            aria-describedby={describedBy([
              "redraftCopy",
              "redraftKeeps",
              bulkChoices.length < 2 ? null : "redraftCompetition",
              "redraftAgain",
            ])}
            data-testid="redraft-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="redraftTitle">Re-Draft — Variant {variant + 1}</b>
            {bulkChoices.length < 2 ? null : (
              <fieldset className="clearscope" data-testid="redraft-competition">
                <legend className="kicker">Which competition</legend>
                {(["all", ...bulkChoices] as BulkCompetition[]).map((choice) => (
                  <label key={choice}>
                    <input
                      type="radio"
                      name="redraft-competition"
                      value={choice}
                      checked={bulkComp === choice}
                      onChange={() => {
                        setBulkComp(choice);
                      }}
                    />
                    {bulkCompetitionWord(choice)}
                  </label>
                ))}
              </fieldset>
            )}
            <p id="redraftCopy" data-testid="redraft-prompt-copy">
              Runs {stageScopeWord} again and produces a <b>different</b> arrangement &mdash; not
              a better one. It obeys every rule this draft obeys and evens out the same figures;
              the officials are simply paired and spread another way.
            </p>
            <p id="redraftKeeps" data-testid="redraft-prompt-keeps">
              <b>Every slot you have confirmed, edited by hand, pushed to TMS or overridden is
              kept exactly as it is.</b> Only what the engine chose is rearranged.
            </p>
            {bulkChoices.length < 2 ? null : (
              <p id="redraftCompetition" data-testid="redraft-prompt-competition">
                This press re-drafts <b>{bulkWord}</b>.
              </p>
            )}
            <p id="redraftAgain">
              Press it again for another variant. Each one is repeatable rather than random, so
              nothing here is a gamble &mdash; but there is no way back to a variant you have
              drafted over, so stop on the one you want.
            </p>
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runReDraft()}
              >
                Run The Re-Draft
              </button>
              <button
                type="button"
                className="gbtn"
                onClick={() => {
                  setRedraftPrompt(false);
                  setFocusRequest({ kind: "element", ids: [REDRAFT_ID] });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {clearPrompt === null ? null : (
          /* The one control on this page that removes the TD's own work, so it says so plainly
             and it says what it will not remove in the same breath. The scope is chosen here
             rather than by two separate buttons: the difference between clearing a day and
             clearing the stage is a decision to be made while reading the sentence, not one to
             be made by which button the hand landed on. */
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="clearTitle"
            aria-describedby={describedBy([
              "clearCopy",
              bulkChoices.length < 2 ? null : "clearCompetition",
            ])}
            data-testid="clear-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="clearTitle">Clear Drafts</b>
            <fieldset className="clearscope">
              <legend className="kicker">What to clear</legend>
              <label>
                <input
                  type="radio"
                  name="clear-scope"
                  value="day"
                  checked={clearPrompt === "day"}
                  onChange={() => {
                    setClearPrompt("day");
                  }}
                />
                This day ({view.scope.day ?? scope.day})
              </label>
              <label>
                <input
                  type="radio"
                  name="clear-scope"
                  value="stage"
                  checked={clearPrompt === "stage"}
                  onChange={() => {
                    setClearPrompt("stage");
                  }}
                />
                {stageScopeWord}
              </label>
            </fieldset>
            {bulkChoices.length < 2 ? null : (
              <fieldset className="clearscope" data-testid="clear-competition">
                <legend className="kicker">Which competition</legend>
                {(["all", ...bulkChoices] as BulkCompetition[]).map((choice) => (
                  <label key={choice}>
                    <input
                      type="radio"
                      name="clear-competition"
                      value={choice}
                      checked={bulkComp === choice}
                      onChange={() => {
                        setBulkComp(choice);
                      }}
                    />
                    {bulkCompetitionWord(choice)}
                  </label>
                ))}
              </fieldset>
            )}
            <p id="clearCopy" data-testid="clear-prompt-copy">
              Clears every unconfirmed drafted appointment for{" "}
              <b>{clearPrompt === "day" ? "this day" : stageScopeWord}</b>, including ones you
              adjusted by hand. Confirmed days, pushed slots and recorded overrides are never
              touched.
            </p>
            {bulkChoices.length < 2 ? null : (
              <p id="clearCompetition" data-testid="clear-prompt-competition">
                This press clears <b>{bulkWord}</b>.
              </p>
            )}
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runClearDrafts(clearPrompt)}
              >
                Clear Them
              </button>
              <button
                type="button"
                className="gbtn"
                onClick={() => {
                  setClearPrompt(null);
                  setFocusRequest({ kind: "element", ids: [CLEAR_DRAFTS_ID] });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {withdraw === null ? null : (
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="wdTitle"
            aria-describedby="wdCopy"
            data-testid="withdraw-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="wdTitle">Withdraw Appointment</b>
            <p id="wdCopy">
              <b>{withdraw.officialName}</b> would be withdrawn from the <b>{withdraw.slot}</b> slot
              of <b>{withdraw.displayMatchNo}</b>, leaving it unfilled. Confirm?
            </p>
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runWithdraw(withdraw)}
              >
                Confirm Withdrawal
              </button>
              <button
                type="button"
                className="gbtn"
                onClick={() => {
                  setWithdraw(null);
                  setFocusRequest({
                    kind: "element",
                    ids: [withdrawButtonId(withdraw.tmsMatchDbId, withdraw.slot)],
                  });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!lockPrompt ? null : (
          /* RUN 82 (§A11) — the lock is a statement about TMS that this screen cannot check, so the
             question puts the one condition that makes it true first: the day is in TMS, and
             published there. Then what locking does, and the way back out, before anything is
             written. The copy is the Technical Delegate's rulings of 16 September 2026.

             RUN 82A — the four sentences are the question's description (each paragraph carries an
             id rather than a wrapper, because the frame's rules reach its children directly); the
             answer that writes is disabled while any other write is out; and focus opens on
             `Cancel` and goes back to `Mark Day Locked` if that is the answer.

             RUN 82D (QA pass 2) — modal to assistive technology (`aria-modal`, with the workbench
             behind it inert) and to the keyboard (Tab stays on its two buttons). And the count is
             what the press covers: a match it will skip — Official in TMS and not confirmed here —
             is left out of it and named in a sentence of its own, which joins the description only
             when there is one. */
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="lockTitle"
            aria-describedby={
              lockSkips.length === 0
                ? "lockScope lockWhen lockEffect lockUndo"
                : "lockScope lockSkipped lockWhen lockEffect lockUndo"
            }
            data-testid="lock-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="lockTitle">Mark {dayLabel(scope.day)} Locked</b>
            <p id="lockScope" data-testid="lock-prompt-scope">
              This covers <b>{countOf(view.fixtures.length - lockSkips.length, "match")}</b> in both
              competitions.
            </p>
            {lockSkips.length === 0 ? null : (
              <p id="lockSkipped" data-testid="lock-prompt-skipped">
                {lockSkipAhead(lockSkips)}
              </p>
            )}
            <p id="lockWhen" data-testid="lock-prompt-when">
              Say this only once these TO, SJ and TJ appointments are entered &mdash; and published
              &mdash; in TMS.
            </p>
            <p id="lockEffect" data-testid="lock-prompt-effect">
              Locked appointments become read-only here and count as the record of who officiated.
            </p>
            <p id="lockUndo" data-testid="lock-prompt-undo">
              To correct a match later, unlock it from its row: it then needs re-entering in TMS,
              and the day locked again.
            </p>
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runLock()}
              >
                Lock The Day
              </button>
              <button
                type="button"
                className="gbtn"
                ref={lockCancelRef}
                onClick={() => {
                  setLockPrompt(false);
                  setFocusRequest({ kind: "element", ids: [LOCK_DAY_ID] });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {unlockPrompt === null ? null : (
          /* RUN 82 (§A11) — reopening a record. It says the three things that change here, and
             then what this screen cannot do for the TD.

             RUN 82A — it no longer says what TMS holds, because nothing here can see TMS: it says
             instead that this screen does not change TMS, and the order of the way back. And it
             warns that the way back closes when TMS marks the match Official — after that the
             match can be neither confirmed nor locked here. Focus, the busy answer and the
             description follow the lock's question above.

             RUN 82D (QA pass 2) — modal as the lock's question is. And the warning says exactly when
             the way back closes: a match confirmed again before TMS marks it Official is still one
             the lock takes; it is the match that goes Official UNCONFIRMED that can no longer be
             confirmed or locked here. */
          <div
            className="overridebox"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="unlockTitle"
            aria-describedby="unlockCopy unlockTms unlockOfficial"
            data-testid="unlock-prompt"
            onKeyDown={keepTabInside}
          >
            <b id="unlockTitle">Unlock {unlockPrompt.displayMatchNo}</b>
            <p id="unlockCopy" data-testid="unlock-prompt-copy">
              Its TO, SJ and TJ become editable again, their confirmation is cleared and the match
              is marked <b>Needs Re-Push</b>.
            </p>
            <p id="unlockTms" data-testid="unlock-prompt-tms">
              This screen does not change TMS. Correct the crew in TMS, then Confirm Day and Mark
              Day Locked again.
            </p>
            <p id="unlockOfficial" data-testid="unlock-prompt-official">
              Correct the crew and confirm it before TMS marks this match Official &mdash; once it
              is Official unconfirmed, it can no longer be confirmed or locked here.
            </p>
            <div className="overridebox__acts">
              <button
                type="button"
                className="gbtn gbtn--solid"
                disabled={busy}
                onClick={() => void runUnlock(unlockPrompt)}
              >
                Unlock The Match
              </button>
              <button
                type="button"
                className="gbtn"
                ref={unlockCancelRef}
                onClick={() => {
                  setUnlockPrompt(null);
                  setFocusRequest({
                    kind: "element",
                    ids: [unlockButtonId(unlockPrompt.tmsMatchDbId)],
                  });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* RUN 84 - the push question: modal the way every question here is, from Confirm Push to
            the answer. Its Cancel gives the focus back to the competition's push control; its
            answer, to the notice or the failure strip once it is closed. */}
        {pushFlow === null ? null : (
          <TmsPushQuestion
            flow={pushFlow}
            batch={
              pushFlow.kind === "confirm"
                ? pushBatchPreview(view.fixtures, pushFlow.competitionId, scope.day)
                : null
            }
            busy={busy}
            onConfirm={() => {
              if (pushFlow.kind === "confirm") void runPush(pushFlow.competitionId);
            }}
            onCancel={() => {
              if (pushFlow.kind !== "confirm") return;
              setPushFlow(null);
              setFocusRequest({ kind: "element", ids: [pushButtonId(pushFlow.competitionId)] });
            }}
            onClose={closePush}
            onReportAgain={() => void reportPushAgain()}
            onKeyDown={keepTabInside}
          />
        )}

        {/* The day push (section 6c): its own question, modal as every question here is, from
            Confirm Push to the answer. Its Cancel gives the focus back to Push Day To TMS; its answer,
            to the notice or the failure strip once it is closed. */}
        {dayFlow === null ? null : (
          <TmsDayPushQuestion
            flow={dayFlow}
            preview={dayFlow.kind === "confirm" ? pushDayPreview(view.fixtures, scope.day) : null}
            busy={busy}
            onConfirm={() => {
              if (dayFlow.kind === "confirm") void runPushDay();
            }}
            onCancel={() => {
              if (dayFlow.kind !== "confirm") return;
              setDayFlow(null);
              setFocusRequest({ kind: "element", ids: [PUSH_DAY_BUTTON_ID] });
            }}
            onClose={closeDayPush}
            onReportAgain={() => void reportDayPushAgain()}
            onKeyDown={keepTabInside}
          />
        )}
      </div>
    </>
  );

  function warnSummaryStrip(): React.JSX.Element {
    return (
      <div className="warnsummary" data-testid="warnings-summary" data-howto="day-warnings">
        <span className="kicker">Day Warnings</span>
        <button
          type="button"
          className={`warncount warncount--hard${warningFocus === "hard" ? " is-on" : ""}`}
          onClick={() => {
            focusWarnings("hard");
          }}
        >
          {counts.hard} rule breach
        </button>
        <button
          type="button"
          className={`warncount warncount--attention${warningFocus === "attention" ? " is-on" : ""}`}
          onClick={() => {
            focusWarnings("attention");
          }}
        >
          {counts.attention} needing attention
        </button>
        <button
          type="button"
          className={`warncount warncount--advisory${warningFocus === "advisory" ? " is-on" : ""}`}
          onClick={() => {
            focusWarnings("advisory");
          }}
        >
          {counts.advisory} advisory
        </button>
        <button
          type="button"
          className={`warncount warncount--anomaly${warningFocus === "anomaly" ? " is-on" : ""}`}
          onClick={() => {
            focusWarnings("anomaly");
          }}
        >
          {counts.anomaly} anomaly note
        </button>
        {warningFocus === null ? null : (
          <span className="warnfilter" data-testid="warning-filter">
            Showing only the matches behind that count.
            <button
              type="button"
              onClick={() => {
                setWarningFocus(null);
              }}
            >
              Show all matches
            </button>
          </span>
        )}
      </div>
    );
  }
}

